import { generateWeeklyTimetableBatches, type WeeklyGenerationOptions } from "./timetableWeeklyGeneration";
import type { TTSwapRequest } from "./api";
import { supabase } from "./supabase";
import {
    timetableDeleteFail,
    timetableDeleteOk,
    type TimetableDeleteResult,
} from "./timetableDeleteErrors";
import { attachMySchoolRoles, TIMETABLE_SCHOOL_SELECT } from "./timetableSchoolList";

export type { TimetableDeleteResult };
import {
    TTSchool,
    TTGradeInfo,
    TTPlace,
    TTClassroom,
    TTSubject,
    TTTeacher,
    TTTeacherDetail,
    TTTeachingAssignment,
    TTSchedule,
    TTTeamTeachingGroup,
    TTConstraintConfig,
    TTTimetable,
    TTAssignmentSummaryItem,
    TTSetupProgress,
    TTSchoolRole,
} from "./api";

/**
 * 시간표(Timetable) 데이터 직결 기능 (Supabase)
 * 단순 읽기, 쓰기, 수정, 삭제(CRUD)를 Django API 대신 Supabase로 직접 요청합니다.
 * 백엔드 부하를 줄이고 응답 속도를 개선하기 위한 목적입니다.
 * 
 * 모든 함수는 성공 시 데이터를, Supabase 장애/오류 시에는 `null`을 반환합니다.
 * `null`을 반환받은 호출부(api.ts)에서는 Django API로 Fallback 해야 합니다.
 */

// ============================================================================
// 1. School (학교)
// ============================================================================

export async function getSchoolsFromSupabase(): Promise<TTSchool[] | null> {
    if (!supabase) return null;
    // Superuser RLS can return every school. Prefer getSchoolsWithRoleFromSupabase()
    // for "내 학교 목록" so unrelated schools are not shown.
    const { data, error } = await supabase
        .from("schedule_manager_school")
        .select(TIMETABLE_SCHOOL_SELECT)
        .order("created_at", { ascending: false });
    if (error) {
        console.warn("Supabase getSchools error:", error);
        return null;
    }
    return data.map(d => ({
        ...d,
        teacher_count: ((d as any).schedule_manager_teacher?.[0]?.count ?? 0) as number,
        classroom_count: ((d as any).schedule_manager_classroom?.[0]?.count ?? 0) as number,
        timetable_count: ((d as any).schedule_manager_timetable?.[0]?.count ?? 0) as number,
        has_active_timetable: false, // will be checked separately if needed
    })) as TTSchool[];
}

export async function createSchoolInSupabase(
    name: string,
    total_grades: number,
    admin_id: number
): Promise<TTSchool | null> {
    if (!supabase) return null;
    // schedule_manager_school 의 created_at/updated_at 은 NOT NULL이고 DEFAULT 가 없어 명시적으로 채운다.
    const now = new Date().toISOString();
    const { data, error } = await supabase
        .from("schedule_manager_school")
        .insert([{ name, total_grades, admin_id, created_at: now, updated_at: now }])
        .select()
        .single();
    if (error) {
        console.warn("Supabase createSchool error:", error);
        return null;
    }
    // 신규 학교 진입 시 대시보드/제약조건 화면에서 constraintconfig가 없으면
    // PostgREST .single()이 406(PGRST116)로 보일 수 있다.
    // 여기서는 시간표 모듈의 기본값을 best-effort로 seed한다.
    try {
        await ensureDefaultConstraintConfigForSchool((data as TTSchool).id);
    } catch (e) {
        console.warn("Supabase ensureDefaultConstraintConfig error:", e);
    }
    return data as TTSchool;
}

export async function getSchoolDetailFromSupabase(id: number): Promise<TTSchool | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_school")
        .select(TIMETABLE_SCHOOL_SELECT)
        .eq("id", id)
        .single();
    if (error) {
        console.warn("Supabase getSchoolDetail error:", error);
        return null;
    }
    return {
        ...data,
        teacher_count: ((data as any).schedule_manager_teacher?.[0]?.count ?? 0) as number,
        classroom_count: ((data as any).schedule_manager_classroom?.[0]?.count ?? 0) as number,
        timetable_count: ((data as any).schedule_manager_timetable?.[0]?.count ?? 0) as number,
        has_active_timetable: false,
    } as TTSchool;
}

export async function updateSchoolInSupabase(
    id: number,
    updates: Partial<{ name: string; total_grades: number }>
): Promise<TTSchool | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_school")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();
    if (error) {
        console.warn("Supabase updateSchool error:", error);
        return null;
    }
    return data as TTSchool;
}

export async function deleteSchoolInSupabase(id: number): Promise<boolean | null> {
    if (!supabase) return null;
    // 학교 삭제는 종속 테이블(schedule/timetable/teacher/classroom/...) 이 NO ACTION FK 라
    // raw DELETE 가 23503 으로 막힌다. SECURITY DEFINER RPC 가 admin 검증 + 의존성 역순
    // 일괄 삭제를 처리한다.
    const { data, error } = await supabase.rpc("delete_school_cascade", {
        p_school_id: id,
    });
    if (error) {
        console.warn("Supabase rpc delete_school_cascade error:", error);
        return null;
    }
    const result = data as { success: boolean; error?: string };
    if (!result?.success) {
        console.warn("Supabase rpc delete_school_cascade failed:", result);
        return null;
    }
    return true;
}

// ============================================================================
// 2. GradeInfo (학년 정보)
// ============================================================================

export async function getGradesFromSupabase(schoolId: number): Promise<TTGradeInfo[] | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_gradeinfo")
        .select("*")
        .eq("school_id", schoolId)
        .order("grade", { ascending: true });
    if (error) {
        console.warn("Supabase getGrades error:", error);
        return null;
    }
    return data as TTGradeInfo[];
}

const GRADE_UPDATE_KEYS = [
    "grade",
    "num_classes",
    "periods_monday",
    "periods_tuesday",
    "periods_wednesday",
    "periods_thursday",
    "periods_friday",
] as const;

type GradeUpdateFields = Pick<
    TTGradeInfo,
    (typeof GRADE_UPDATE_KEYS)[number]
>;

function pickGradeUpdates(updates: Partial<TTGradeInfo>): Partial<GradeUpdateFields> {
    const out: Partial<GradeUpdateFields> = {};
    for (const key of GRADE_UPDATE_KEYS) {
        if (Object.prototype.hasOwnProperty.call(updates, key) && updates[key] !== undefined) {
            out[key] = updates[key] as number;
        }
    }
    return out;
}

/**
 * 학년(grade)의 학급 행을 numClasses에 맞춤 (양방향).
 * - class_num 1…numClasses 누락분 insert
 * - class_num > numClasses (또는 numClasses=0이면 해당 학년 전부) delete
 * - 삭제 시 종속 데이터(배정/시간표/담임 등)가 있으면 FK 오류로 실패
 */
async function reconcileClassroomsForGrade(
    schoolId: number,
    grade: number,
    numClasses: number
): Promise<TimetableDeleteResult> {
    if (!supabase) {
        return { ok: false, message: "학급 동기화에 실패했습니다. 잠시 후 다시 시도해 주세요." };
    }
    const target = Math.max(0, Math.floor(numClasses));

    const { data: rows, error: selErr } = await supabase
        .from("schedule_manager_classroom")
        .select("id, class_num")
        .eq("school_id", schoolId)
        .eq("grade", grade);
    if (selErr) {
        console.warn("Supabase reconcileClassrooms select error:", selErr);
        return { ok: false, message: "학급 정보를 불러오지 못했습니다." };
    }

    const list = (rows ?? []) as { id: number; class_num: number }[];

    // 초과 학급 먼저 삭제 (반 수 감소·학년 삭제 시 gradeinfo와 어긋나지 않도록)
    const excess = list.filter((r) => r.class_num > target || r.class_num < 1);
    for (const room of excess) {
        const { error: delErr } = await supabase
            .from("schedule_manager_classroom")
            .delete()
            .eq("id", room.id);
        if (delErr) {
            console.warn("Supabase reconcileClassrooms delete error:", delErr);
            return timetableDeleteFail("classroom", delErr);
        }
    }

    if (target < 1) return timetableDeleteOk();

    const remaining = new Set(
        list.filter((r) => r.class_num >= 1 && r.class_num <= target).map((r) => r.class_num)
    );
    const toInsert: { school_id: number; grade: number; class_num: number }[] = [];
    for (let classNum = 1; classNum <= target; classNum++) {
        if (!remaining.has(classNum)) {
            toInsert.push({ school_id: schoolId, grade, class_num: classNum });
        }
    }
    if (toInsert.length > 0) {
        const { error: insErr } = await supabase.from("schedule_manager_classroom").insert(toInsert);
        if (insErr) {
            console.warn("Supabase reconcileClassrooms insert error:", insErr);
            return { ok: false, message: "학급을 추가하지 못했습니다." };
        }
    }
    return timetableDeleteOk();
}

/**
 * gradeinfo에 없는 학년의 고아 학급을 안전하게 정리 (종속 있으면 스킵).
 */
async function pruneOrphanClassroomsForSchool(schoolId: number, knownGrades: Set<number>): Promise<void> {
    if (!supabase) return;
    const { data: rows, error } = await supabase
        .from("schedule_manager_classroom")
        .select("id, grade, class_num")
        .eq("school_id", schoolId);
    if (error) {
        console.warn("Supabase pruneOrphanClassrooms select error:", error);
        return;
    }
    const orphanGrades = new Set(
        (rows ?? [])
            .map((r: { grade: number }) => r.grade)
            .filter((g: number) => !knownGrades.has(g))
    );
    for (const grade of orphanGrades) {
        const result = await reconcileClassroomsForGrade(schoolId, grade, 0);
        if (!result.ok) {
            console.warn(
                `Supabase pruneOrphanClassrooms: grade ${grade} skipped —`,
                result.message
            );
        }
    }
}

/**
 * 학년 설정과 학급 행을 동기화 (누락 추가 + 초과/고아 정리).
 * 교사 탭·학년 탭 로드 시 기존 데이터 보정용.
 */
export async function ensureClassroomsForGradesInSupabase(schoolId: number): Promise<void> {
    const grades = await getGradesFromSupabase(schoolId);
    // gradeinfo 조회 실패 시 고아 정리까지 하면 전체 학급을 지울 수 있으므로 중단
    if (grades === null) return;
    const known = new Set(grades.map((g) => g.grade));
    for (const g of grades) {
        const result = await reconcileClassroomsForGrade(schoolId, g.grade, g.num_classes);
        if (!result.ok) {
            console.warn(
                `Supabase ensureClassrooms grade ${g.grade} reconcile skipped —`,
                result.message
            );
        }
    }
    await pruneOrphanClassroomsForSchool(schoolId, known);
}

export async function createGradeInSupabase(
    schoolId: number,
    payload: Omit<TTGradeInfo, "id" | "total_periods">
): Promise<TTGradeInfo | null> {
    if (!supabase) return null;
    const insertPayload = pickGradeUpdates(payload as Partial<TTGradeInfo>);
    const { data, error } = await supabase
        .from("schedule_manager_gradeinfo")
        .insert([{ ...insertPayload, school_id: schoolId }])
        .select()
        .single();
    if (error) {
        console.warn("Supabase createGrade error:", error);
        return null;
    }
    const row = data as TTGradeInfo & { school_id?: number };
    const sync = await reconcileClassroomsForGrade(schoolId, row.grade, row.num_classes);
    if (!sync.ok) {
        await supabase.from("schedule_manager_gradeinfo").delete().eq("id", row.id);
        throw new Error(sync.message);
    }
    return row as TTGradeInfo;
}

export async function updateGradeInSupabase(
    gradeId: number,
    updates: Partial<TTGradeInfo>
): Promise<TTGradeInfo | null> {
    if (!supabase) return null;

    const allowed = pickGradeUpdates(updates);
    // 학년 번호는 생성 후 변경하지 않음 (학급 grade 컬럼과 불일치 방지)
    delete (allowed as { grade?: number }).grade;

    if (Object.keys(allowed).length === 0) {
        const { data: existing, error: existingErr } = await supabase
            .from("schedule_manager_gradeinfo")
            .select("*")
            .eq("id", gradeId)
            .single();
        if (existingErr) {
            console.warn("Supabase updateGrade fetch error:", existingErr);
            return null;
        }
        return existing as TTGradeInfo;
    }

    const { data: current, error: curErr } = await supabase
        .from("schedule_manager_gradeinfo")
        .select("*")
        .eq("id", gradeId)
        .single();
    if (curErr || !current) {
        console.warn("Supabase updateGrade fetch error:", curErr);
        return null;
    }

    const sid = (current as TTGradeInfo & { school_id: number }).school_id;
    const grade = (current as TTGradeInfo).grade;
    const currentNum = (current as TTGradeInfo).num_classes;
    const targetNum = allowed.num_classes ?? currentNum;

    // 반 수 감소 시: gradeinfo 변경 전에 초과 학급 삭제 (실패하면 설정 미변경)
    if (targetNum < currentNum) {
        const prune = await reconcileClassroomsForGrade(sid, grade, targetNum);
        if (!prune.ok) throw new Error(prune.message);
    }

    const { data, error } = await supabase
        .from("schedule_manager_gradeinfo")
        .update(allowed)
        .eq("id", gradeId)
        .select()
        .single();
    if (error) {
        console.warn("Supabase updateGrade error:", error);
        if (targetNum < currentNum) {
            await reconcileClassroomsForGrade(sid, grade, currentNum);
        }
        return null;
    }

    const row = data as TTGradeInfo & { school_id?: number };
    const sync = await reconcileClassroomsForGrade(sid, row.grade, row.num_classes);
    if (!sync.ok) {
        await supabase
            .from("schedule_manager_gradeinfo")
            .update({ num_classes: currentNum })
            .eq("id", gradeId);
        await reconcileClassroomsForGrade(sid, grade, currentNum);
        throw new Error(sync.message);
    }
    return row as TTGradeInfo;
}

export async function deleteGradeInSupabase(gradeId: number): Promise<TimetableDeleteResult | null> {
    if (!supabase) return null;

    const { data: current, error: curErr } = await supabase
        .from("schedule_manager_gradeinfo")
        .select("id, grade, school_id")
        .eq("id", gradeId)
        .single();
    if (curErr || !current) {
        console.warn("Supabase deleteGrade fetch error:", curErr);
        return timetableDeleteFail("grade", curErr);
    }

    const sid = (current as { school_id: number }).school_id;
    const grade = (current as { grade: number }).grade;
    const sync = await reconcileClassroomsForGrade(sid, grade, 0);
    if (!sync.ok) return sync;

    const { error } = await supabase
        .from("schedule_manager_gradeinfo")
        .delete()
        .eq("id", gradeId);
    if (error) {
        console.warn("Supabase deleteGrade error:", error);
        // 학급은 이미 지워졌을 수 있음 — gradeinfo 삭제만 실패한 경우 학급 복구는 하지 않음
        // (ensure 시 다시 맞춰짐). 실패 메시지는 grade 기준으로 반환.
        return timetableDeleteFail("grade", error);
    }
    return timetableDeleteOk();
}

// ============================================================================
// 3. Place (장소)
// ============================================================================

export async function getPlacesFromSupabase(schoolId: number): Promise<TTPlace[] | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_place")
        .select("*")
        .eq("school_id", schoolId)
        .order("name", { ascending: true });
    if (error) {
        console.warn("Supabase getPlaces error:", error);
        return null;
    }
    return data as TTPlace[];
}

export async function createPlaceInSupabase(
    schoolId: number,
    payload: Omit<TTPlace, "id">
): Promise<TTPlace | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_place")
        .insert([{ ...payload, school_id: schoolId }])
        .select()
        .single();
    if (error) {
        console.warn("Supabase createPlace error:", error);
        return null;
    }
    return data as TTPlace;
}

export async function updatePlaceInSupabase(
    placeId: number,
    updates: Partial<TTPlace>
): Promise<TTPlace | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_place")
        .update(updates)
        .eq("id", placeId)
        .select()
        .single();
    if (error) {
        console.warn("Supabase updatePlace error:", error);
        return null;
    }
    return data as TTPlace;
}

export async function deletePlaceInSupabase(placeId: number): Promise<TimetableDeleteResult | null> {
    if (!supabase) return null;
    const { error } = await supabase
        .from("schedule_manager_place")
        .delete()
        .eq("id", placeId);
    if (error) {
        console.warn("Supabase deletePlace error:", error);
        return timetableDeleteFail("place", error);
    }
    return timetableDeleteOk();
}

// ============================================================================
// 4. Classroom (학급)
// ============================================================================

export async function getClassroomsFromSupabase(schoolId: number): Promise<TTClassroom[] | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_classroom")
        .select("*")
        .eq("school_id", schoolId)
        .order("grade", { ascending: true })
        .order("class_num", { ascending: true });
    if (error) {
        console.warn("Supabase getClassrooms error:", error);
        return null;
    }
    return data as TTClassroom[];
}

export async function deleteClassroomInSupabase(classroomId: number): Promise<TimetableDeleteResult | null> {
    if (!supabase) return null;
    const { error } = await supabase
        .from("schedule_manager_classroom")
        .delete()
        .eq("id", classroomId);
    if (error) {
        console.warn("Supabase deleteClassroom error:", error);
        return timetableDeleteFail("classroom", error);
    }
    return timetableDeleteOk();
}

// ============================================================================
// 5. Subject (과목)
// ============================================================================

export async function getSubjectsFromSupabase(schoolId: number): Promise<TTSubject[] | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_subject")
        .select("*")
        .eq("school_id", schoolId)
        .order("name", { ascending: true });
    if (error) {
        console.warn("Supabase getSubjects error:", error);
        return null;
    }
    return data as TTSubject[];
}

export async function createSubjectInSupabase(
    schoolId: number,
    payload: { name: string; default_place?: number | null }
): Promise<TTSubject | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_subject")
        .insert([{ ...payload, school_id: schoolId }])
        .select()
        .single();
    if (error) {
        console.warn("Supabase createSubject error:", error);
        return null;
    }
    return data as TTSubject;
}

export async function updateSubjectInSupabase(
    subjectId: number,
    updates: Partial<TTSubject>
): Promise<TTSubject | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_subject")
        .update(updates)
        .eq("id", subjectId)
        .select()
        .single();
    if (error) {
        console.warn("Supabase updateSubject error:", error);
        return null;
    }
    return data as TTSubject;
}

export async function deleteSubjectInSupabase(subjectId: number): Promise<TimetableDeleteResult | null> {
    if (!supabase) return null;
    const { error } = await supabase
        .from("schedule_manager_subject")
        .delete()
        .eq("id", subjectId);
    if (error) {
        console.warn("Supabase deleteSubject error:", error);
        return timetableDeleteFail("subject", error);
    }
    return timetableDeleteOk();
}

/**
 * Django subject_manage 의 bulk add 분기와 동등.
 *
 * - `namesRaw` 는 콤마 또는 줄바꿈으로 구분된 자유 입력 문자열.
 * - 빈 문자열·공백·이미 존재하는 이름은 자동 무시.
 * - 새로 들어간 과목만 추가하고, 항상 학교 전체의 최신 과목 목록을 반환한다.
 */
export async function bulkCreateSubjectsInSupabase(
    schoolId: number,
    namesRaw: string
): Promise<TTSubject[] | null> {
    if (!supabase) return null;

    const incoming = Array.from(
        new Set(
            namesRaw
                .split(/[,\n\r]+/)
                .map((s) => s.trim())
                .filter((s) => s.length > 0)
        )
    );
    if (incoming.length === 0) {
        return getSubjectsFromSupabase(schoolId);
    }

    const { data: existing, error: existingErr } = await supabase
        .from("schedule_manager_subject")
        .select("name")
        .eq("school_id", schoolId)
        .in("name", incoming);
    if (existingErr) {
        console.warn("Supabase bulkCreateSubjects existing error:", existingErr);
        return null;
    }
    const existingNames = new Set((existing || []).map((r: { name: string }) => r.name));
    const toInsert = incoming.filter((n) => !existingNames.has(n));

    if (toInsert.length > 0) {
        const rows = toInsert.map((name) => ({ school_id: schoolId, name }));
        const { error: insErr } = await supabase
            .from("schedule_manager_subject")
            .insert(rows);
        if (insErr) {
            console.warn("Supabase bulkCreateSubjects insert error:", insErr);
            return null;
        }
    }

    return getSubjectsFromSupabase(schoolId);
}

// ============================================================================
// 5b. Timeslot (시간대 - 학년별 교시 시간)
// ============================================================================

export type TTTimeslot = {
    id: number;
    grade: number;
    day: string;
    period: number;
    start_time: string;
    end_time: string;
};

export async function getTimeslotsFromSupabase(schoolId: number): Promise<TTTimeslot[] | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_timeslot")
        .select("id, grade, day, period, start_time, end_time")
        .eq("school_id", schoolId)
        .order("grade", { ascending: true })
        .order("day", { ascending: true })
        .order("period", { ascending: true });
    if (error) {
        console.warn("Supabase getTimeslots error:", error);
        return null;
    }
    return data as TTTimeslot[];
}

/**
 * 한 학년의 timeslot 목록을 통째로 교체한다 (Django bulk endpoint와 동등 의미).
 * - (school_id, grade) 의 기존 timeslot 모두 삭제 후 새 slots 일괄 INSERT.
 * - 중간에 실패해도 RLS 정책상 학교 admin 권한이 보장되므로 안전.
 *
 * tt_timeslot RLS 에 UPDATE 정책이 없어 upsert 는 쓰지 않는다.
 */
export async function bulkCreateTimeslotsInSupabase(
    schoolId: number,
    grade: number,
    slots: { day: string; period: number; start_time: string; end_time: string }[]
): Promise<TTTimeslot[] | null> {
    if (!supabase) return null;

    const { error: delErr } = await supabase
        .from("schedule_manager_timeslot")
        .delete()
        .eq("school_id", schoolId)
        .eq("grade", grade);
    if (delErr) {
        console.warn("Supabase bulkCreateTimeslots delete error:", delErr);
        return null;
    }

    if (slots.length === 0) return [];

    const now = new Date().toISOString();
    const rows = slots.map((s) => ({
        school_id: schoolId,
        grade,
        day: s.day,
        period: s.period,
        start_time: s.start_time,
        end_time: s.end_time,
        created_at: now,
        updated_at: now,
    }));

    const { error: insErr } = await supabase
        .from("schedule_manager_timeslot")
        .insert(rows);
    if (insErr) {
        console.warn("Supabase bulkCreateTimeslots insert error:", insErr);
        return null;
    }

    const { data, error: selErr } = await supabase
        .from("schedule_manager_timeslot")
        .select("id, grade, day, period, start_time, end_time")
        .eq("school_id", schoolId)
        .eq("grade", grade)
        .order("day", { ascending: true })
        .order("period", { ascending: true });
    if (selErr) {
        console.warn("Supabase bulkCreateTimeslots select error:", selErr);
        return null;
    }
    return data as TTTimeslot[];
}

// ============================================================================
// 6. Teacher (교사)
// ============================================================================

export async function getTeachersFromSupabase(schoolId: number): Promise<TTTeacher[] | null> {
    if (!supabase) return null;
    // 단일 쿼리로 선생님 전체 조회 (담임, 담당과목, 불가시간 포함)
    const { data, error } = await supabase
        .from("schedule_manager_teacher")
        .select(`
            id, school_id, name, homeroom_id, user_id,
            homeroom:schedule_manager_classroom(grade, class_num),
            subjects:schedule_manager_teacher_subjects(subject:schedule_manager_subject(id, name)),
            unavailable_times:schedule_manager_teacherunavailabletime(count)
        `)
        .eq("school_id", schoolId)
        .order("name", { ascending: true });

    if (error) {
        console.warn("Supabase getTeachers error:", error);
        return null;
    }

    return data.map((item: any) => {
        // Extract homeroom_display
        let homeroom_display = null;
        if (item.homeroom) {
            const hr = Array.isArray(item.homeroom) ? item.homeroom[0] : item.homeroom;
            if (hr) homeroom_display = `${hr.grade}-${hr.class_num}`;
        }

        // Extract subject_names
        let subject_names: string[] = [];
        if (item.subjects && Array.isArray(item.subjects)) {
            subject_names = item.subjects
                .map((s: any) => Array.isArray(s.subject) ? s.subject[0]?.name : s.subject?.name)
                .filter(Boolean);
        }

        // Extract unavailable_count
        let unavailable_count = 0;
        if (item.unavailable_times && Array.isArray(item.unavailable_times) && item.unavailable_times[0]) {
            unavailable_count = item.unavailable_times[0].count || 0;
        }

        return {
            id: item.id,
            school_id: item.school_id,
            name: item.name,
            color: "#DFDFDF",
            is_part_time: false,
            homeroom: item.homeroom_id || null,
            homeroom_display,
            subject_names,
            subject_ids: (item.subjects ?? []).map((link: any) => {
                const subject = Array.isArray(link.subject) ? link.subject[0] : link.subject;
                return subject?.id;
            }).filter((id: unknown): id is number => typeof id === "number"),
            unavailable_count,
            has_user: item.user_id != null,
            user_id: item.user_id ?? null,
        };
    }) as unknown as TTTeacher[];
}

export async function deleteTeacherInSupabase(teacherId: number): Promise<TimetableDeleteResult | null> {
    if (!supabase) return null;
    const { error } = await supabase
        .from("schedule_manager_teacher")
        .delete()
        .eq("id", teacherId);
    if (error) {
        console.warn("Supabase deleteTeacher error:", error);
        return timetableDeleteFail("teacher", error);
    }
    return timetableDeleteOk();
}

/**
 * 단일 교사 상세 조회 (TTTeacherDetail 형태).
 * Django 시절 `get_teacher_info` 응답과 동일한 모양을 만들기 위해
 * subjects(M2M), unavailable_times, homeroom, user 정보를 조인해 가져온다.
 */
export async function getTeacherDetailFromSupabase(teacherId: number): Promise<TTTeacherDetail | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_teacher")
        .select(`
            id, school_id, name, homeroom_id, user_id,
            homeroom:schedule_manager_classroom(grade, class_num),
            subjects:schedule_manager_teacher_subjects(subject_id, subject:schedule_manager_subject(name)),
            unavailable_times:schedule_manager_teacherunavailabletime(id, day, period)
        `)
        .eq("id", teacherId)
        .single();

    if (error) {
        console.warn("Supabase getTeacherDetail error:", error);
        return null;
    }

    const item: any = data;
    let homeroom_display: string | null = null;
    if (item.homeroom) {
        const hr = Array.isArray(item.homeroom) ? item.homeroom[0] : item.homeroom;
        if (hr) homeroom_display = `${hr.grade}-${hr.class_num}`;
    }

    const subjectIds: number[] = Array.isArray(item.subjects)
        ? item.subjects.map((s: any) => s.subject_id).filter((v: any) => typeof v === "number")
        : [];
    const subject_names: string[] = Array.isArray(item.subjects)
        ? item.subjects
            .map((s: any) => (Array.isArray(s.subject) ? s.subject[0]?.name : s.subject?.name))
            .filter(Boolean)
        : [];

    const unavailable: { id: number; day: string; period: number }[] = Array.isArray(item.unavailable_times)
        ? item.unavailable_times.map((u: any) => ({ id: u.id, day: u.day, period: u.period }))
        : [];

    return {
        id: item.id,
        name: item.name,
        homeroom: item.homeroom_id ?? null,
        homeroom_display,
        subject_names,
        unavailable_count: unavailable.length,
        subjects: subjectIds,
        unavailable_times: unavailable,
        has_user: item.user_id != null,
        user_id: item.user_id ?? null,
    } as TTTeacherDetail;
}

type TeacherWritePayload = {
    name?: string;
    homeroom?: number | null;
    subjects?: number[];
    unavailable_times?: { day: string; period: number }[];
};

/**
 * 교사 신규 등록.
 *
 * Django 시절 `teacher_manage` POST 분기 동작을 재현한다.
 * 1) 동일 학교 내 이름 중복은 DB UNIQUE 제약 (school_id, name) 으로 차단된다.
 * 2) 교사 행을 먼저 만들고
 * 3) subjects(M2M), unavailable_times 를 별도 insert 한다.
 *
 * Supabase 클라이언트는 트랜잭션을 묶을 수 없어 단계별 실패 시 정합성이 깨질 수 있으나,
 * 모든 단계는 멱등하지 않은 단순 insert 라 재시도가 안전하다.
 */
export async function createTeacherInSupabase(
    schoolId: number,
    payload: TeacherWritePayload & { name: string }
): Promise<TTTeacherDetail | null> {
    if (!supabase) return null;

    const { data: created, error: insertErr } = await supabase
        .from("schedule_manager_teacher")
        .insert([{
            school_id: schoolId,
            name: payload.name,
            homeroom_id: payload.homeroom ?? null,
        }])
        .select("id")
        .single();

    if (insertErr || !created) {
        console.warn("Supabase createTeacher error:", insertErr);
        return null;
    }
    const teacherId: number = created.id;

    if (Array.isArray(payload.subjects) && payload.subjects.length > 0) {
        const subjectRows = payload.subjects.map((sid) => ({ teacher_id: teacherId, subject_id: sid }));
        const { error: subjErr } = await supabase
            .from("schedule_manager_teacher_subjects")
            .insert(subjectRows);
        if (subjErr) {
            console.warn("Supabase createTeacher subjects error:", subjErr);
            return null;
        }
    }

    if (Array.isArray(payload.unavailable_times) && payload.unavailable_times.length > 0) {
        const seen = new Set<string>();
        const rows = payload.unavailable_times
            .filter((u) => {
                const key = `${u.day}-${u.period}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .map((u) => ({ teacher_id: teacherId, day: u.day, period: u.period }));
        if (rows.length > 0) {
            const { error: unavailErr } = await supabase
                .from("schedule_manager_teacherunavailabletime")
                .insert(rows);
            if (unavailErr) {
                console.warn("Supabase createTeacher unavailable_times error:", unavailErr);
                return null;
            }
        }
    }

    return getTeacherDetailFromSupabase(teacherId);
}

/**
 * 교사 정보 수정.
 *
 * Django 시절 `update_teacher` 동작을 따른다.
 * - name/homeroom 은 부분 업데이트 (필드가 들어왔을 때만)
 * - subjects 가 들어오면 기존 M2M 전체 삭제 후 재삽입 (`set()` 의미)
 * - unavailable_times 가 들어오면 기존 행 전체 삭제 후 재삽입
 */
export async function updateTeacherInSupabase(
    schoolId: number,
    teacherId: number,
    payload: TeacherWritePayload
): Promise<TTTeacherDetail | null> {
    if (!supabase) return null;

    const teacherUpdates: Record<string, unknown> = {};
    if (typeof payload.name === "string") teacherUpdates.name = payload.name;
    if (Object.prototype.hasOwnProperty.call(payload, "homeroom")) {
        teacherUpdates.homeroom_id = payload.homeroom ?? null;
    }

    if (Object.keys(teacherUpdates).length > 0) {
        const { error: updErr } = await supabase
            .from("schedule_manager_teacher")
            .update(teacherUpdates)
            .eq("id", teacherId)
            .eq("school_id", schoolId);
        if (updErr) {
            console.warn("Supabase updateTeacher base error:", updErr);
            return null;
        }
    }

    if (Array.isArray(payload.subjects)) {
        const { error: delErr } = await supabase
            .from("schedule_manager_teacher_subjects")
            .delete()
            .eq("teacher_id", teacherId);
        if (delErr) {
            console.warn("Supabase updateTeacher subjects clear error:", delErr);
            return null;
        }
        if (payload.subjects.length > 0) {
            const rows = payload.subjects.map((sid) => ({ teacher_id: teacherId, subject_id: sid }));
            const { error: insErr } = await supabase
                .from("schedule_manager_teacher_subjects")
                .insert(rows);
            if (insErr) {
                console.warn("Supabase updateTeacher subjects insert error:", insErr);
                return null;
            }
        }
    }

    if (Array.isArray(payload.unavailable_times)) {
        const { error: delErr } = await supabase
            .from("schedule_manager_teacherunavailabletime")
            .delete()
            .eq("teacher_id", teacherId);
        if (delErr) {
            console.warn("Supabase updateTeacher unavailable clear error:", delErr);
            return null;
        }
        if (payload.unavailable_times.length > 0) {
            const seen = new Set<string>();
            const rows = payload.unavailable_times
                .filter((u) => {
                    const key = `${u.day}-${u.period}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                })
                .map((u) => ({ teacher_id: teacherId, day: u.day, period: u.period }));
            if (rows.length > 0) {
                const { error: insErr } = await supabase
                    .from("schedule_manager_teacherunavailabletime")
                    .insert(rows);
                if (insErr) {
                    console.warn("Supabase updateTeacher unavailable insert error:", insErr);
                    return null;
                }
            }
        }
    }

    return getTeacherDetailFromSupabase(teacherId);
}

export async function getTeacherUnavailableTimesFromSupabase(teacherIds: number[]): Promise<{ teacher_id: number; day: string; period: number }[] | null> {
    if (!supabase || teacherIds.length === 0) return null;
    const { data, error } = await supabase
        .from("schedule_manager_teacherunavailabletime")
        .select("teacher_id, day, period")
        .in("teacher_id", teacherIds);
    if (error) {
        console.warn("Supabase getTeacherUnavailableTimes error:", error);
        return null;
    }
    return data as { teacher_id: number; day: string; period: number }[];
}

// ============================================================================
// 7. TeachingAssignment (배정)
// ============================================================================

export async function getAssignmentsFromSupabase(schoolId: number): Promise<TTTeachingAssignment[] | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_teachingassignment")
        .select(`
            *,
            teacher:schedule_manager_teacher(name),
            subject:schedule_manager_subject(name),
            classroom:schedule_manager_classroom(grade, class_num),
            place:schedule_manager_place(name)
        `)
        .eq("school_id", schoolId)
        .order("classroom_id", { ascending: true })
        .order("subject_id", { ascending: true });
    if (error) {
        console.warn("Supabase getAssignments error:", error);
        return null;
    }
    return data.map((d: any) => {
        const teacherObj = Array.isArray(d.teacher) ? d.teacher[0] : d.teacher;
        const subjectObj = Array.isArray(d.subject) ? d.subject[0] : d.subject;
        const classroomObj = Array.isArray(d.classroom) ? d.classroom[0] : d.classroom;
        const placeObj = Array.isArray(d.place) ? d.place[0] : d.place;

        return {
            ...d,
            teacher: d.teacher_id,
            teacher_name: teacherObj?.name || "",
            subject: d.subject_id,
            subject_name: subjectObj?.name || "",
            classroom: d.classroom_id,
            classroom_display: classroomObj ? `${classroomObj.grade}-${classroomObj.class_num}` : "",
            place: d.place_id,
            place_name: placeObj?.name || null,
        }
    }) as TTTeachingAssignment[];
}

/**
 * 수업배정(TeachingAssignment) 생성/갱신.
 *
 * Django 시절 `teaching_assignment_manage` 뷰의 `get_or_create` + 갱신 의미를 보존하기 위해
 * unique 제약 (school_id, teacher_id, classroom_id, subject_id) 을 기준으로 upsert 합니다.
 * - 존재하지 않으면 새로 생성
 * - 존재하면 hours_per_week / place / is_block / block_size / single_hours 갱신
 *
 * 호출부(api.ts)는 한 번의 호출로 여러 학급에 배정할 수 있으므로,
 * classrooms 배열의 각 항목에 대해 한 행씩 upsert 합니다.
 */
export async function createAssignmentInSupabase(
    schoolId: number,
    payload: {
        teacher: number;
        subject: number;
        classrooms: number[];
        hours_per_week: number;
        is_block?: boolean;
        block_size?: number;
        single_hours?: number;
        place?: number | null;
    }
): Promise<TTTeachingAssignment[] | null> {
    if (!supabase) return null;
    if (!Array.isArray(payload.classrooms) || payload.classrooms.length === 0) {
        return [];
    }

    const isBlock = payload.is_block === true;
    const blockSize = isBlock ? Math.max(0, payload.block_size ?? 2) : 2;
    const singleHours = isBlock ? Math.max(0, payload.single_hours ?? 0) : 0;
    const hoursPerWeek = Math.max(0, Number.isFinite(payload.hours_per_week) ? payload.hours_per_week : 0);
    const placeId = payload.place == null ? null : payload.place;

    const records = payload.classrooms.map((classroomId) => ({
        school_id: schoolId,
        teacher_id: payload.teacher,
        subject_id: payload.subject,
        classroom_id: classroomId,
        place_id: placeId,
        hours_per_week: hoursPerWeek,
        is_block: isBlock,
        block_size: blockSize,
        single_hours: singleHours,
    }));

    const { data, error } = await supabase
        .from("schedule_manager_teachingassignment")
        .upsert(records, {
            onConflict: "school_id,teacher_id,classroom_id,subject_id",
        })
        .select(`
            *,
            teacher:schedule_manager_teacher(name),
            subject:schedule_manager_subject(name),
            classroom:schedule_manager_classroom(grade, class_num),
            place:schedule_manager_place(name)
        `);

    if (error) {
        console.warn("Supabase createAssignment error:", error);
        return null;
    }

    return (data ?? []).map((d: any) => {
        const teacherObj = Array.isArray(d.teacher) ? d.teacher[0] : d.teacher;
        const subjectObj = Array.isArray(d.subject) ? d.subject[0] : d.subject;
        const classroomObj = Array.isArray(d.classroom) ? d.classroom[0] : d.classroom;
        const placeObj = Array.isArray(d.place) ? d.place[0] : d.place;
        return {
            ...d,
            teacher: d.teacher_id,
            teacher_name: teacherObj?.name || "",
            subject: d.subject_id,
            subject_name: subjectObj?.name || "",
            classroom: d.classroom_id,
            classroom_display: classroomObj ? `${classroomObj.grade}-${classroomObj.class_num}` : "",
            place: d.place_id,
            place_name: placeObj?.name || null,
        };
    }) as TTTeachingAssignment[];
}

export async function updateAssignmentInSupabase(
    assignmentId: number,
    updates: Partial<TTTeachingAssignment>
): Promise<TTTeachingAssignment | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_teachingassignment")
        .update(updates)
        .eq("id", assignmentId)
        .select()
        .single();
    if (error) {
        console.warn("Supabase updateAssignment error:", error);
        return null;
    }
    return data as TTTeachingAssignment;
}

export async function deleteAssignmentInSupabase(assignmentId: number): Promise<TimetableDeleteResult | null> {
    if (!supabase) return null;
    const { error } = await supabase
        .from("schedule_manager_teachingassignment")
        .delete()
        .eq("id", assignmentId);
    if (error) {
        console.warn("Supabase deleteAssignment error:", error);
        return timetableDeleteFail("assignment", error);
    }
    return timetableDeleteOk();
}

// ============================================================================
// 8. Filtered Schedules (고정수업조회 등을 위해 View 사용)
// ============================================================================

export async function getFixedSchedulesFromSupabase(schoolId: number): Promise<TTSchedule[] | null> {
    if (!supabase) return null;
    // 047_timetable_schedules_view.sql 에 작성한 tt_schedules_view 사용
    //
    // 커리큘럼 '고정시간표' 목록은 schedule_type 으로 식별한다.
    // is_fixed 컬럼은 생성된 시간표의 lock 상태(REG 잠금 등)도 포함하므로 사용하지 않는다.
    const { data, error } = await supabase
        .from("tt_schedules_view")
        .select("*")
        .eq("school_id", schoolId)
        .in("schedule_type", ["FIX", "HOM", "SPE"]);

    if (error) {
        console.warn("Supabase getFixedSchedules error:", error);
        return null;
    }

    return data.map((d: any) => ({
        ...d,
        teacher: d.teacher_id,
        subject: d.subject_id,
        classroom: d.classroom_id,
        classroom_display: d.classroom_name || (d.classroom_grade ? `${d.classroom_grade}-${d.classroom_class_num}` : ""),
        schedule_type_display: d.schedule_type === "HOM" ? "담임" : "고정"
    })) as TTSchedule[];
}

/**
 * 고정수업 행 한 건을 조회용 모양(TTSchedule)으로 다시 가져온다.
 * Create/Update 후 응답 형태를 맞추기 위해 join 포함한 select 를 별도 호출한다.
 */
async function fetchScheduleAsTT(scheduleId: number): Promise<TTSchedule | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_schedule")
        .select(`
            id, school_id, schedule_type, is_fixed, day, period, description, target_grade, max_students,
            teacher_id, subject_id, classroom_id, place_id,
            teacher:schedule_manager_teacher(name),
            subject:schedule_manager_subject(name),
            classroom:schedule_manager_classroom(grade, class_num),
            place:schedule_manager_place(name)
        `)
        .eq("id", scheduleId)
        .single();

    if (error || !data) {
        console.warn("Supabase fetchScheduleAsTT error:", error);
        return null;
    }

    const d: any = data;
    const teacherObj = Array.isArray(d.teacher) ? d.teacher[0] : d.teacher;
    const subjectObj = Array.isArray(d.subject) ? d.subject[0] : d.subject;
    const classroomObj = Array.isArray(d.classroom) ? d.classroom[0] : d.classroom;
    const placeObj = Array.isArray(d.place) ? d.place[0] : d.place;
    const typeDisplay = d.schedule_type === "HOM"
        ? "담임"
        : d.schedule_type === "SPE"
            ? "특별"
            : d.schedule_type === "REG"
                ? "정규수업"
                : "고정";

    return {
        id: d.id,
        teacher: d.teacher_id,
        teacher_name: teacherObj?.name || "",
        subject: d.subject_id,
        subject_name: subjectObj?.name || "",
        classroom: d.classroom_id ?? null,
        classroom_display: classroomObj ? `${classroomObj.grade}-${classroomObj.class_num}` : null,
        place: d.place_id ?? null,
        place_name: placeObj?.name || null,
        day: d.day,
        period: d.period,
        schedule_type: d.schedule_type,
        schedule_type_display: typeDisplay,
        is_fixed: !!d.is_fixed,
        description: d.description ?? null,
        target_grade: d.target_grade ?? null,
        max_students: d.max_students ?? null,
    } as TTSchedule;
}

type FixedSchedulePayload = {
    teacher?: number;
    subject?: number;
    classroom?: number | null;
    day?: string;
    period?: number;
    place?: number | null;
    description?: string | null;
    target_grade?: number | null;
    max_students?: number | null;
    schedule_type?: string; // 일반적으로 'FIX'
};

/**
 * 같은 시간대에 충돌하는 schedule 이 있는지 검사한다.
 * - 같은 (school, teacher, day, period) 에 다른 행이 있으면 교사 중복
 * - 같은 (school, classroom, day, period) 에 다른 행이 있으면 학급 중복
 * Django `fixed_schedule_manage` 의 검증 로직과 동일.
 */
const CURRICULUM_FIXED_TYPES = ["FIX", "HOM", "SPE"] as const;
const GENERATED_OVERWRITE_TYPES = ["REG", "TEM"] as const;

/**
 * 교육과정 고정(FIX/HOM/SPE)을 넣을 학급 슬롯에 남아 있는 생성분(REG/TEM)을 제거한다.
 * 잠긴 REG(is_fixed=true)도 포함 — 교육과정 고정이 정본이고, 공존 시 생성기가 학급 슬롯 중복으로 실패한다.
 */
async function removeGeneratedSchedulesAtClassSlot(
    schoolId: number,
    classroomId: number,
    day: string,
    period: number
): Promise<boolean> {
    if (!supabase) return false;

    const { data: rows, error } = await supabase
        .from("schedule_manager_schedule")
        .select("id")
        .eq("school_id", schoolId)
        .eq("classroom_id", classroomId)
        .eq("day", day)
        .eq("period", period)
        .in("schedule_type", [...GENERATED_OVERWRITE_TYPES]);

    if (error) {
        console.warn("Supabase removeGeneratedAtClassSlot select error:", error);
        return false;
    }

    const ids = (rows || []).map((r: { id: number }) => r.id);
    for (const id of ids) {
        const { error: junctionError } = await supabase
            .from("schedule_manager_timetable_schedules")
            .delete()
            .eq("schedule_id", id);
        if (junctionError) {
            console.warn("Supabase removeGeneratedAtClassSlot junction error:", junctionError);
            return false;
        }
        const { error: delErr } = await supabase
            .from("schedule_manager_schedule")
            .delete()
            .eq("id", id);
        if (delErr) {
            console.warn("Supabase removeGeneratedAtClassSlot delete error:", delErr);
            return false;
        }
    }
    return true;
}

async function findFixedScheduleConflicts(
    schoolId: number,
    teacherId: number,
    classroomId: number | null,
    day: string,
    period: number,
    excludeScheduleId?: number
): Promise<{ teacherConflict: boolean; classroomConflict: boolean } | null> {
    if (!supabase) return null;

    // 충돌 검사는 커리큘럼의 고정수업(FIX/HOM/SPE) 끼리만 한다.
    // 자동 생성된 REG/TEM 행은 아래 removeGeneratedSchedulesAtClassSlot 으로 정리한다.
    const CURRICULUM_TYPES = [...CURRICULUM_FIXED_TYPES];

    const teacherQuery = supabase
        .from("schedule_manager_schedule")
        .select("id")
        .eq("school_id", schoolId)
        .eq("teacher_id", teacherId)
        .eq("day", day)
        .eq("period", period)
        .in("schedule_type", CURRICULUM_TYPES)
        .limit(1);

    const classroomQuery = classroomId
        ? supabase
            .from("schedule_manager_schedule")
            .select("id")
            .eq("school_id", schoolId)
            .eq("classroom_id", classroomId)
            .eq("day", day)
            .eq("period", period)
            .in("schedule_type", CURRICULUM_TYPES)
            .limit(1)
        : Promise.resolve({ data: [] as { id: number }[], error: null });

    const [tRes, cRes] = await Promise.all([
        excludeScheduleId ? teacherQuery.neq("id", excludeScheduleId) : teacherQuery,
        excludeScheduleId && classroomId
            ? (classroomQuery as any).neq("id", excludeScheduleId)
            : classroomQuery,
    ]);

    if ((tRes as any).error || (cRes as any).error) {
        console.warn("Supabase fixed schedule conflict check error:", (tRes as any).error, (cRes as any).error);
        return null;
    }

    return {
        teacherConflict: Array.isArray((tRes as any).data) && (tRes as any).data.length > 0,
        classroomConflict: Array.isArray((cRes as any).data) && (cRes as any).data.length > 0,
    };
}

/**
 * 고정수업(FIX) 한 건을 추가한다.
 *
 * Django `fixed_schedule_manage` 의 단일 (teacher × classroom) 분기 동작을 보존한다.
 * 같은 시간대 같은 교사/학급의 다른 schedule 이 있으면 충돌 에러를 던진다.
 *
 * 호출부(TimetableCurriculum.tsx)는 schedule_type = 'FIX' 만 보내므로 FIX 만 처리한다.
 * 담임시간(HOM) 일괄 배정이나 특별수업(SPE) 은 별도 API 가 담당한다.
 */
export async function createFixedScheduleInSupabase(
    schoolId: number,
    payload: FixedSchedulePayload
): Promise<TTSchedule | null> {
    if (!supabase) return null;

    if (
        typeof payload.teacher !== "number" ||
        typeof payload.subject !== "number" ||
        typeof payload.classroom !== "number" ||
        typeof payload.day !== "string" ||
        typeof payload.period !== "number"
    ) {
        throw new Error("필수 필드가 누락되었습니다 (teacher / subject / classroom / day / period).");
    }

    const conflicts = await findFixedScheduleConflicts(
        schoolId,
        payload.teacher,
        payload.classroom,
        payload.day,
        payload.period
    );
    if (!conflicts) return null;
    if (conflicts.teacherConflict) {
        throw new Error(`해당 교사는 ${payload.day}요일 ${payload.period}교시에 이미 다른 고정수업이 있습니다.`);
    }
    if (conflicts.classroomConflict) {
        throw new Error(`해당 학급은 ${payload.day}요일 ${payload.period}교시에 이미 다른 고정수업이 있습니다.`);
    }

    const cleared = await removeGeneratedSchedulesAtClassSlot(
        schoolId,
        payload.classroom,
        payload.day,
        payload.period
    );
    if (!cleared) return null;

    const { data: inserted, error: insertErr } = await supabase
        .from("schedule_manager_schedule")
        .insert([{
            school_id: schoolId,
            teacher_id: payload.teacher,
            subject_id: payload.subject,
            classroom_id: payload.classroom,
            place_id: payload.place ?? null,
            day: payload.day,
            period: payload.period,
            schedule_type: payload.schedule_type === "FIX" ? "FIX" : (payload.schedule_type || "FIX"),
            is_fixed: true,
            description: payload.description ?? null,
            target_grade: payload.target_grade ?? null,
            max_students: payload.max_students ?? null,
        }])
        .select("id")
        .single();

    if (insertErr || !inserted) {
        console.warn("Supabase createFixedSchedule insert error:", insertErr);
        return null;
    }

    return fetchScheduleAsTT(inserted.id);
}

/**
 * 고정수업(FIX) 한 건을 수정한다.
 *
 * 변경 후의 (teacher, day, period) 또는 (classroom, day, period) 가 다른 schedule 과 충돌하면 에러.
 * 자기 자신은 충돌 검사에서 제외한다.
 *
 * payload 로 들어온 키만 부분 업데이트한다.
 */
export async function updateFixedScheduleInSupabase(
    schoolId: number,
    scheduleId: number,
    payload: FixedSchedulePayload
): Promise<TTSchedule | null> {
    if (!supabase) return null;

    const { data: existing, error: existingErr } = await supabase
        .from("schedule_manager_schedule")
        .select("teacher_id, classroom_id, day, period, school_id")
        .eq("id", scheduleId)
        .single();

    if (existingErr || !existing) {
        console.warn("Supabase updateFixedSchedule existing fetch error:", existingErr);
        return null;
    }
    if (existing.school_id !== schoolId) {
        throw new Error("학교 권한이 일치하지 않습니다.");
    }

    const nextTeacher = typeof payload.teacher === "number" ? payload.teacher : existing.teacher_id;
    const nextClassroom = Object.prototype.hasOwnProperty.call(payload, "classroom")
        ? (payload.classroom ?? null)
        : existing.classroom_id;
    const nextDay = typeof payload.day === "string" ? payload.day : existing.day;
    const nextPeriod = typeof payload.period === "number" ? payload.period : existing.period;

    const conflicts = await findFixedScheduleConflicts(
        schoolId,
        nextTeacher,
        nextClassroom,
        nextDay,
        nextPeriod,
        scheduleId
    );
    if (!conflicts) return null;
    if (conflicts.teacherConflict) {
        throw new Error(`해당 교사는 ${nextDay}요일 ${nextPeriod}교시에 이미 다른 고정수업이 있습니다.`);
    }
    if (conflicts.classroomConflict) {
        throw new Error(`해당 학급은 ${nextDay}요일 ${nextPeriod}교시에 이미 다른 고정수업이 있습니다.`);
    }

    if (nextClassroom != null) {
        const cleared = await removeGeneratedSchedulesAtClassSlot(
            schoolId,
            nextClassroom,
            nextDay,
            nextPeriod
        );
        if (!cleared) return null;
    }

    const updates: Record<string, unknown> = {};
    if (typeof payload.teacher === "number") updates.teacher_id = payload.teacher;
    if (typeof payload.subject === "number") updates.subject_id = payload.subject;
    if (Object.prototype.hasOwnProperty.call(payload, "classroom")) updates.classroom_id = payload.classroom ?? null;
    if (typeof payload.day === "string") updates.day = payload.day;
    if (typeof payload.period === "number") updates.period = payload.period;
    if (Object.prototype.hasOwnProperty.call(payload, "place")) updates.place_id = payload.place ?? null;
    if (Object.prototype.hasOwnProperty.call(payload, "description")) updates.description = payload.description ?? null;
    if (Object.prototype.hasOwnProperty.call(payload, "target_grade")) updates.target_grade = payload.target_grade ?? null;
    if (Object.prototype.hasOwnProperty.call(payload, "max_students")) updates.max_students = payload.max_students ?? null;
    if (typeof payload.schedule_type === "string") updates.schedule_type = payload.schedule_type;

    if (Object.keys(updates).length > 0) {
        const { error: updErr } = await supabase
            .from("schedule_manager_schedule")
            .update(updates)
            .eq("id", scheduleId)
            .eq("school_id", schoolId);
        if (updErr) {
            console.warn("Supabase updateFixedSchedule update error:", updErr);
            return null;
        }
    }

    return fetchScheduleAsTT(scheduleId);
}

/**
 * Django `fixed_schedule_manage` 의 schedule_type='HOM' 분기와 동등.
 *
 * 1. 같은 (school, day, period) 에 HOM 행이 있으면 throw
 * 2. 학교 내 담임 지정된 교사 (teacher.homeroom_id IS NOT NULL) 모두 fetch
 * 3. 담임시간 과목 get-or-create
 * 4. 각 교사별로 같은 (school, teacher, day, period) 충돌 검사 → 없으면 HOM insert, 있으면 skipped 에 이름 push
 */
export async function bulkCreateHomeroomFixedSchedulesInSupabase(
    schoolId: number,
    payload: { day: string; period: number; subject_name?: string }
): Promise<{ created: TTSchedule[]; created_count: number; skipped: string[] } | null> {
    if (!supabase) return null;

    const subjectName = (payload.subject_name && payload.subject_name.trim()) || "담임시간";

    const { data: existingHom, error: existingErr } = await supabase
        .from("schedule_manager_schedule")
        .select("id")
        .eq("school_id", schoolId)
        .eq("day", payload.day)
        .eq("period", payload.period)
        .eq("schedule_type", "HOM")
        .limit(1);
    if (existingErr) {
        console.warn("Supabase homeroomBulk existingHom error:", existingErr);
        return null;
    }
    if (existingHom && existingHom.length > 0) {
        throw new Error(`${payload.day}요일 ${payload.period}교시에 이미 담임시간이 설정되어 있습니다.`);
    }

    const { data: teachers, error: teachersErr } = await supabase
        .from("schedule_manager_teacher")
        .select("id, name, homeroom_id")
        .eq("school_id", schoolId)
        .not("homeroom_id", "is", null);
    if (teachersErr) {
        console.warn("Supabase homeroomBulk teachers error:", teachersErr);
        return null;
    }
    if (!teachers || teachers.length === 0) {
        throw new Error("담임교사가 설정된 교사가 없습니다. 먼저 담임교사를 설정해주세요.");
    }

    let subjectId: number;
    const { data: subjectRow } = await supabase
        .from("schedule_manager_subject")
        .select("id")
        .eq("school_id", schoolId)
        .eq("name", subjectName)
        .maybeSingle();
    if (subjectRow) {
        subjectId = (subjectRow as { id: number }).id;
    } else {
        const { data: created, error: subjErr } = await supabase
            .from("schedule_manager_subject")
            .insert([{ school_id: schoolId, name: subjectName }])
            .select("id")
            .single();
        if (subjErr || !created) {
            console.warn("Supabase homeroomBulk subject create error:", subjErr);
            return null;
        }
        subjectId = (created as { id: number }).id;
    }

    // 교육과정 HOM이 정본이므로, 해당 학급 슬롯의 생성분(REG/TEM·잠금 포함)을 먼저 제거한다.
    for (const t of teachers as any[]) {
        if (t.homeroom_id == null) continue;
        const cleared = await removeGeneratedSchedulesAtClassSlot(
            schoolId,
            t.homeroom_id as number,
            payload.day,
            payload.period
        );
        if (!cleared) return null;
    }

    const teacherIds = teachers.map((t: any) => t.id as number);
    const { data: conflictRows, error: conflictErr } = await supabase
        .from("schedule_manager_schedule")
        .select("teacher_id")
        .eq("school_id", schoolId)
        .eq("day", payload.day)
        .eq("period", payload.period)
        .in("teacher_id", teacherIds)
        .in("schedule_type", [...CURRICULUM_FIXED_TYPES]);
    if (conflictErr) {
        console.warn("Supabase homeroomBulk conflict scan error:", conflictErr);
        return null;
    }
    const conflictTeacherIds = new Set((conflictRows || []).map((r: any) => r.teacher_id as number));

    const created: TTSchedule[] = [];
    const skipped: string[] = [];
    const toInsert: any[] = [];

    for (const t of teachers as any[]) {
        if (conflictTeacherIds.has(t.id)) {
            skipped.push(t.name);
            continue;
        }
        toInsert.push({
            school_id: schoolId,
            teacher_id: t.id,
            classroom_id: t.homeroom_id,
            subject_id: subjectId,
            day: payload.day,
            period: payload.period,
            schedule_type: "HOM",
            is_fixed: true,
        });
    }

    if (toInsert.length > 0) {
        const { data: insertedRows, error: insErr } = await supabase
            .from("schedule_manager_schedule")
            .insert(toInsert)
            .select("id");
        if (insErr) {
            console.warn("Supabase homeroomBulk insert error:", insErr);
            return null;
        }
        const ids = (insertedRows || []).map((r: any) => r.id as number);
        const fetched = await Promise.all(ids.map((id) => fetchScheduleAsTT(id)));
        for (const f of fetched) {
            if (f) created.push(f);
        }
    }

    return { created, created_count: created.length, skipped };
}

export async function deleteScheduleInSupabase(scheduleId: number): Promise<boolean | null> {
    if (!supabase) return null;

    // 1. junction 테이블(Timetable <-> Schedule)에서 참조 먼저 삭제하여 FK 제약 조건 위반 방지
    const { error: junctionError } = await supabase
        .from("schedule_manager_timetable_schedules")
        .delete()
        .eq("schedule_id", scheduleId);

    if (junctionError) {
        console.warn("Supabase deleteSchedule junction cleanup error:", junctionError);
        // junction 삭제 실패 시 본 테이블 삭제도 실패할 가능성이 높으므로 null 반환
        return null;
    }

    // 2. 실제 schedule 레코드 삭제
    const { error, count } = await supabase
        .from("schedule_manager_schedule")
        .delete({ count: 'planned' })
        .eq("id", scheduleId);

    if (error) {
        console.warn("Supabase deleteSchedule error:", error);
        return null;
    }
    // count가 0이면 삭제되지 않은 것이므로 false 반환
    return count !== 0;
}

// ============================================================================
// 9. Timetable (시간표 목록)
// ============================================================================

export async function getTimetablesFromSupabase(schoolId: number): Promise<TTTimetable[] | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_timetable")
        .select("*, schedule_manager_timetable_schedules(count)")
        .eq("school_id", schoolId)
        .order("created_at", { ascending: false });
    if (error) {
        console.warn("Supabase getTimetables error:", error);
        return null;
    }
    return data.map(d => ({
        ...d,
        schedule_count: ((d as any).schedule_manager_timetable_schedules?.[0]?.count ?? 0) as number,
    })) as TTTimetable[];
}

/**
 * 단일 timetable 행을 join 없이 다시 조회해 TTTimetable 모양으로 돌려준다.
 * Activate/Deactivate 후 갱신된 행을 응답하기 위해 사용한다.
 */
async function fetchTimetableAsTT(timetableId: number): Promise<TTTimetable | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_timetable")
        .select("*, schedule_manager_timetable_schedules(count)")
        .eq("id", timetableId)
        .single();
    if (error || !data) {
        console.warn("Supabase fetchTimetableAsTT error:", error);
        return null;
    }
    const d: any = data;
    return {
        ...d,
        schedule_count: (d.schedule_manager_timetable_schedules?.[0]?.count ?? 0) as number,
    } as TTTimetable;
}

/**
 * 시간표 활성화.
 *
 * Django `activate_timetable` 의 동작을 보존:
 * 1) 같은 학교의 활성 시간표 모두 비활성화
 * 2) 대상 시간표만 활성화
 *
 * Supabase 클라이언트는 트랜잭션을 묶을 수 없어 1) 과 2) 사이에 끼어들 가능성이 있으나,
 * 활성화 정책상 짧은 시간 동안 비활성 상태가 되더라도 마지막 update 후 정상화되므로 안전하다.
 */
export async function activateTimetableInSupabase(
    schoolId: number,
    timetableId: number
): Promise<TTTimetable | null> {
    if (!supabase) return null;

    const { error: deactErr } = await supabase
        .from("schedule_manager_timetable")
        .update({ is_active: false })
        .eq("school_id", schoolId)
        .eq("is_active", true);

    if (deactErr) {
        console.warn("Supabase activateTimetable previous-deactivate error:", deactErr);
        return null;
    }

    const { error: actErr } = await supabase
        .from("schedule_manager_timetable")
        .update({ is_active: true })
        .eq("id", timetableId)
        .eq("school_id", schoolId);

    if (actErr) {
        console.warn("Supabase activateTimetable activate error:", actErr);
        return null;
    }

    return fetchTimetableAsTT(timetableId);
}

/**
 * 시간표 비활성화. 단순히 is_active=false 로 set 한다.
 */
export async function deactivateTimetableInSupabase(
    schoolId: number,
    timetableId: number
): Promise<TTTimetable | null> {
    if (!supabase) return null;

    const { error } = await supabase
        .from("schedule_manager_timetable")
        .update({ is_active: false })
        .eq("id", timetableId)
        .eq("school_id", schoolId);

    if (error) {
        console.warn("Supabase deactivateTimetable error:", error);
        return null;
    }

    return fetchTimetableAsTT(timetableId);
}

/**
 * Django `copy_timetable` 와 동등.
 *
 * - schedule 본체는 복제하지 않고 schedule_manager_timetable_schedules 의 m2m 링크만 복제한다.
 * - 새 시간표는 항상 비활성(is_active=false) 으로 만든다.
 * - name 인자가 있으면 그대로 사용, 없으면 `"{원본명} (복사본)"` 으로 fallback.
 *
 * 트랜잭션이 없어 m2m insert 가 실패하면 새로 만든 timetable 행을 best-effort 로 삭제 시도한다.
 */
export async function copyTimetableInSupabase(
    schoolId: number,
    timetableId: number,
    name?: string
): Promise<TTTimetable | null> {
    if (!supabase) return null;

    const { data: original, error: originalErr } = await supabase
        .from("schedule_manager_timetable")
        .select("id, school_id, name, start_date, end_date")
        .eq("id", timetableId)
        .single();
    if (originalErr || !original) {
        console.warn("Supabase copyTimetable fetch original error:", originalErr);
        return null;
    }
    if ((original as any).school_id !== schoolId) {
        throw new Error("학교 권한이 일치하지 않습니다.");
    }

    const newName = (name && name.trim()) || `${(original as any).name} (복사본)`;
    const now = new Date().toISOString();

    const { data: created, error: createErr } = await supabase
        .from("schedule_manager_timetable")
        .insert([{
            school_id: schoolId,
            name: newName,
            start_date: (original as any).start_date,
            end_date: (original as any).end_date,
            is_active: false,
            created_at: now,
            updated_at: now,
        }])
        .select("id")
        .single();
    if (createErr || !created) {
        console.warn("Supabase copyTimetable insert error:", createErr);
        return null;
    }
    const newId = (created as { id: number }).id;

    const { data: links, error: linksErr } = await supabase
        .from("schedule_manager_timetable_schedules")
        .select("schedule_id")
        .eq("timetable_id", timetableId);
    if (linksErr) {
        console.warn("Supabase copyTimetable links fetch error:", linksErr);
        await supabase.from("schedule_manager_timetable").delete().eq("id", newId);
        return null;
    }

    if (links && links.length > 0) {
        const linkRows = links.map((l: any) => ({ timetable_id: newId, schedule_id: l.schedule_id }));
        const { error: linkInsErr } = await supabase
            .from("schedule_manager_timetable_schedules")
            .insert(linkRows);
        if (linkInsErr) {
            console.warn("Supabase copyTimetable link insert error:", linkInsErr);
            await supabase.from("schedule_manager_timetable").delete().eq("id", newId);
            return null;
        }
    }

    return fetchTimetableAsTT(newId);
}

export async function deleteTimetableInSupabase(timetableId: number): Promise<TimetableDeleteResult | null> {
    if (!supabase) return null;
    // First delete M2M records to avoid FK constraint violation
    const { error: m2mError } = await supabase
        .from("schedule_manager_timetable_schedules")
        .delete()
        .eq("timetable_id", timetableId);
    if (m2mError) {
        console.warn("Supabase deleteTimetable M2M cleanup error:", m2mError);
        return timetableDeleteFail("timetable", m2mError);
    }
    const { error } = await supabase
        .from("schedule_manager_timetable")
        .delete()
        .eq("id", timetableId);
    if (error) {
        console.warn("Supabase deleteTimetable error:", error);
        return timetableDeleteFail("timetable", error);
    }
    return timetableDeleteOk();
}

// ============================================================================
// 10. ConstraintConfig (제약조건)
// ============================================================================

export async function getConstraintConfigFromSupabase(schoolId: number): Promise<TTConstraintConfig | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_constraintconfig")
        .select("*")
        .eq("school_id", schoolId)
        .maybeSingle();

    if (error) {
        console.warn("Supabase getConstraintConfig error:", error);
        return null;
    }
    return (data ?? null) as TTConstraintConfig | null;
}

async function ensureDefaultConstraintConfigForSchool(schoolId: number): Promise<void> {
    if (!supabase) return;

    // 이미 있으면 그대로 둔다.
    const { data: existing } = await supabase
        .from("schedule_manager_constraintconfig")
        .select("id")
        .eq("school_id", schoolId)
        .maybeSingle();
    if (existing) return;

    const now = new Date().toISOString();
    const defaults = {
        max_consecutive_periods: 3,
        teacher_max_daily_periods: 6,
        equalize_daily_hours: true,
        equalize_undesirable_periods: true,
        undesirable_periods: [],
        distribute_same_subject: true,
        custom_rules: {},
        created_at: now,
        updated_at: now,
        school_id: schoolId,
    };

    const { error } = await supabase
        .from("schedule_manager_constraintconfig")
        .insert([defaults]);

    // seed 실패는 학교 생성 자체를 막으면 안 되므로, 여기서는 로그만 남긴다.
    if (error) {
        console.warn("Supabase ensureDefaultConstraintConfig insert error:", error);
    }
}

export async function updateConstraintConfigInSupabase(
    schoolId: number,
    updates: Partial<TTConstraintConfig>
): Promise<TTConstraintConfig | null> {
    if (!supabase) return null;

    // 먼저 존재하는지 확인
    const { data: existing } = await supabase
        .from("schedule_manager_constraintconfig")
        .select("id")
        .eq("school_id", schoolId)
        .maybeSingle();

    if (existing) {
        const { data: updated, error } = await supabase
            .from("schedule_manager_constraintconfig")
            .update(updates)
            .eq("school_id", schoolId)
            .select()
            .single();
        if (error) {
            console.warn("Supabase updateConstraint error:", error);
            return null;
        }
        return updated as TTConstraintConfig;
    } else {
        // 없으면 생성 (insert)
        const { data: newlyCreated, error } = await supabase
            .from("schedule_manager_constraintconfig")
            .insert([{ ...updates, school_id: schoolId }])
            .select()
            .single();
        if (error) {
            console.warn("Supabase insertConstraint error:", error);
            return null;
        }
        return newlyCreated as TTConstraintConfig;
    }
}
// ============================================================================
// 11. Advanced Views & RPCs
// ============================================================================

const EMPTY_SETUP_PROGRESS: TTSetupProgress = {
    grades: false,
    classrooms: false,
    teachers: false,
    subjects: false,
    places: false,
    timeslots: false,
    assignments: false,
    constraints: false,
    all_assigned: false,
    unassigned_classrooms: [],
    ready_to_generate: false,
};

async function safeGetSetupProgress(schoolId: number): Promise<TTSetupProgress> {
    if (!supabase) return EMPTY_SETUP_PROGRESS;
    const { data, error } = await supabase.rpc("get_setup_progress", { p_school_id: schoolId });
    if (error) {
        console.warn("Supabase rpc get_setup_progress error:", error);
        return EMPTY_SETUP_PROGRESS;
    }
    return (data ?? EMPTY_SETUP_PROGRESS) as TTSetupProgress;
}

export async function getSchoolSummaryFromSupabase(schoolId: number) {
    if (!supabase) return null;

    // 1. tt_school_summary_view 호출
    const { data: summaryData, error: summaryError } = await supabase
        .from("tt_school_summary_view")
        .select("*")
        .eq("school_id", schoolId)
        .single();
    if (summaryError) {
        console.warn("Supabase summary view error:", summaryError);
        return null; // Fallback to Django if view not found
    }

    const myRole = await getSchoolRoleFromSupabase(schoolId).catch(() => null);
    const isLimitedAccess = myRole === "teacher" || myRole === "viewer";

    // 2. get_setup_progress RPC 호출
    // 연결 교사/보기 권한 사용자는 설정 진행률 RPC에서 막힐 수 있다.
    // 학교 진입 자체는 허용되어야 하므로 기본값으로 대시보드를 계속 렌더링한다.
    const progressData = isLimitedAccess ? EMPTY_SETUP_PROGRESS : await safeGetSetupProgress(schoolId);

    // 3. getTimetables
    const timetables = await getTimetablesFromSupabase(schoolId).catch(() => null) || [];

    // 4. getAssignments
    const assignments = isLimitedAccess ? [] : await getAssignmentsFromSupabase(schoolId).catch(() => null) || [];
    const constraints = isLimitedAccess ? null : await getConstraintConfigFromSupabase(schoolId).catch(() => null);

    // Django Response Format과 임시 호환 (필요 시 API 타입에 맞춰 매핑)
    return {
        school: {
            id: summaryData.school_id,
            name: summaryData.name,
            total_grades: summaryData.total_grades,
            my_role: myRole ?? undefined,
        },
        grades: [], // For summary view, usually just simple count is enough, but API requires array
        classrooms: [],
        teachers: [],
        subjects: [],
        places: [],
        assignments: assignments,
        timetables: timetables,
        constraints,
        setup_progress: progressData
    };
}

export async function getAssignmentSummaryFromSupabase(schoolId: number): Promise<TTAssignmentSummaryItem[] | null> {
    if (!supabase) return null;

    const [{ data: classrooms, error: classroomErr }, { data: gradeInfos, error: gradeErr }, { data: assignments, error: assignmentErr }, { data: fixedSchedules, error: fixedErr }, { data: ttGroups, error: groupErr }, { data: ttClassrooms, error: ttClassroomErr }] = await Promise.all([
        supabase
            .from("schedule_manager_classroom")
            .select("id, grade, class_num")
            .eq("school_id", schoolId),
        supabase
            .from("schedule_manager_gradeinfo")
            .select("grade, periods_monday, periods_tuesday, periods_wednesday, periods_thursday, periods_friday")
            .eq("school_id", schoolId),
        supabase
            .from("schedule_manager_teachingassignment")
            .select("classroom_id, hours_per_week")
            .eq("school_id", schoolId),
        supabase
            .from("schedule_manager_schedule")
            .select("classroom_id")
            .eq("school_id", schoolId)
            .in("schedule_type", ["FIX", "HOM", "SPE"]),
        supabase
            .from("schedule_manager_teamteachinggroup")
            .select("id, hours_per_week")
            .eq("school_id", schoolId),
        supabase
            .from("schedule_manager_teamteachinggroup_classrooms")
            .select("teamteachinggroup_id, classroom_id"),
    ]);

    if (classroomErr || gradeErr || assignmentErr || fixedErr || groupErr || ttClassroomErr) {
        console.warn("Supabase getAssignmentSummary error:", {
            classroomErr, gradeErr, assignmentErr, fixedErr, groupErr, ttClassroomErr,
        });
        return null;
    }

    const gradePeriodMap = new Map<number, number>();
    for (const g of gradeInfos || []) {
        gradePeriodMap.set(
            g.grade,
            (g.periods_monday ?? 0) +
            (g.periods_tuesday ?? 0) +
            (g.periods_wednesday ?? 0) +
            (g.periods_thursday ?? 0) +
            (g.periods_friday ?? 0)
        );
    }

    const assignmentHourMap = new Map<number, number>();
    for (const a of assignments || []) {
        const prev = assignmentHourMap.get(a.classroom_id) ?? 0;
        assignmentHourMap.set(a.classroom_id, prev + (a.hours_per_week ?? 0));
    }

    const fixedHourMap = new Map<number, number>();
    for (const fs of fixedSchedules || []) {
        const prev = fixedHourMap.get(fs.classroom_id) ?? 0;
        fixedHourMap.set(fs.classroom_id, prev + 1);
    }

    const ttGroupHourMap = new Map<number, number>();
    for (const g of ttGroups || []) {
        ttGroupHourMap.set(g.id, g.hours_per_week ?? 0);
    }
    const ttHourMap = new Map<number, number>();
    for (const tc of ttClassrooms || []) {
        const hours = ttGroupHourMap.get(tc.teamteachinggroup_id) ?? 0;
        const prev = ttHourMap.get(tc.classroom_id) ?? 0;
        ttHourMap.set(tc.classroom_id, prev + hours);
    }

    const results: TTAssignmentSummaryItem[] = (classrooms || []).map((c) => {
        const totalPeriods = gradePeriodMap.get(c.grade) ?? 0;
        const assignedHours = assignmentHourMap.get(c.id) ?? 0;
        const fixedHours = fixedHourMap.get(c.id) ?? 0;
        const ttHours = ttHourMap.get(c.id) ?? 0;
        const total = assignedHours + fixedHours + ttHours;
        return {
            classroom_id: c.id,
            display: `${c.grade}-${c.class_num}`,
            grade: c.grade,
            class_num: c.class_num,
            total_periods: totalPeriods,
            assigned_hours: assignedHours,
            fixed_hours: fixedHours + ttHours,
            total,
            remaining: totalPeriods - total,
        };
    });

    results.sort((a, b) => (a.grade - b.grade) || (a.class_num - b.class_num));
    return results;
}

export async function getUnassignedListFromSupabase(schoolId: number, timetableId: number) {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("get_unassigned_list", {
        p_school_id: schoolId,
        p_timetable_id: timetableId
    });

    if (error) {
        console.warn("Supabase rpc get_unassigned_list error:", error);
        return null;
    }

    // Map the returned data to include classroom_display
    return (data || []).map((d: any) => ({
        ...d,
        classroom_display: d.classroom_grade ? `${d.classroom_grade}-${d.classroom_class_num}` : ""
    }));
}

export async function getTimetableSchedulesFromSupabase(schoolId: number, timetableId: number) {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("get_timetable_schedules", {
        p_school_id: schoolId,
        p_timetable_id: timetableId
    });

    if (error) {
        console.warn("Supabase rpc get_timetable_schedules error:", error);
        return null;
    }

    const schedules = Array.isArray(data) ? data : [];
    return schedules.map((s: any) => ({
        ...s,
        classroom_display:
            s.classroom_display
            || s.classroom_name
            || (s.classroom_grade != null && s.classroom_class_num != null
                ? `${s.classroom_grade}-${s.classroom_class_num}`
                : s.target_grade != null ? `${s.target_grade}학년` : ""),
        schedule_type_display:
            s.schedule_type === "REG" ? "정규수업"
            : s.schedule_type === "FIX" ? "고정수업"
            : s.schedule_type === "HOM" ? "담임시간"
            : "특별수업",
    }));
}

// ============================================================================
// 12. Edit Operations (RPC) - Django API 우회, Supabase 직접 실행
// ============================================================================

export async function swapSchedulesInSupabase(
    schoolId: number, sourceId: number, targetId: number, options?: { overrideFixed?: boolean }
): Promise<{ success: boolean; error?: string; errors?: { code?: string; message?: string; field?: string }[] } | null> {
    if (!supabase) return null;
    const rpcName = options?.overrideFixed ? "swap_schedules_override" : "swap_schedules";
    const { data, error } = await supabase.rpc(rpcName, {
        p_school_id: schoolId, p_source_id: sourceId, p_target_id: targetId
    });
    if (error) { console.warn(`Supabase rpc ${rpcName} error:`, error); return null; }
    return data as { success: boolean; error?: string; errors?: { code?: string; message?: string; field?: string }[] };
}

export async function moveScheduleInSupabase(
    schoolId: number, scheduleId: number, toDay: string, toPeriod: number, options?: { overrideFixed?: boolean }
): Promise<{ success: boolean; error?: string; errors?: { code?: string; message?: string; field?: string }[] } | null> {
    if (!supabase) return null;
    const rpcName = options?.overrideFixed ? "move_schedule_override" : "move_schedule";
    const { data, error } = await supabase.rpc(rpcName, {
        p_school_id: schoolId, p_schedule_id: scheduleId, p_to_day: toDay, p_to_period: toPeriod
    });
    if (error) { console.warn(`Supabase rpc ${rpcName} error:`, error); return null; }
    return data as { success: boolean; error?: string; errors?: { code?: string; message?: string; field?: string }[] };
}

export async function toggleScheduleFixedInSupabase(
    schoolId: number, scheduleId: number
): Promise<{ success: boolean; is_fixed?: boolean; error?: string } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("toggle_schedule_fixed", {
        p_school_id: schoolId, p_schedule_id: scheduleId
    });
    if (error) { console.warn("Supabase rpc toggle_schedule_fixed error:", error); return null; }
    return data as { success: boolean; is_fixed?: boolean; error?: string };
}

export async function removeScheduleInSupabase(
    schoolId: number, scheduleId: number, options?: { overrideFixed?: boolean }
): Promise<{ success: boolean; error?: string } | null> {
    if (!supabase) return null;
    const rpcName = options?.overrideFixed ? "remove_schedule_override" : "remove_schedule";
    const { data, error } = await supabase.rpc(rpcName, {
        p_school_id: schoolId, p_schedule_id: scheduleId
    });
    if (error) { console.warn(`Supabase rpc ${rpcName} error:`, error); return null; }
    return data as { success: boolean; error?: string };
}

export async function manualAssignInSupabase(
    schoolId: number, timetableId: number, assignmentId: number, day: string, period: number
    , expectedClassroomId?: number
): Promise<{ success: boolean; schedule_id?: number; error?: string; errors?: { code?: string; message?: string; field?: string }[] } | null> {
    if (!supabase) return null;
    const payloadNew = {
        p_school_id: schoolId, p_timetable_id: timetableId, p_assignment_id: assignmentId, p_day: day, p_period: period,
        p_expected_classroom_id: expectedClassroomId ?? null
    };
    const { data, error } = await supabase.rpc("manual_assign_schedule", payloadNew);
    if (!error) {
        return data as { success: boolean; schedule_id?: number; error?: string; errors?: { code?: string; message?: string; field?: string }[] };
    }
    // Backward compatibility: DB migration not yet applied (function signature mismatch)
    if ((error as any)?.code === 'PGRST202') {
        const payloadOld = {
            p_school_id: schoolId, p_timetable_id: timetableId, p_assignment_id: assignmentId, p_day: day, p_period: period
        };
        const { data: dataOld, error: errorOld } = await supabase.rpc("manual_assign_schedule", payloadOld);
        if (!errorOld) {
            return dataOld as { success: boolean; schedule_id?: number; error?: string; errors?: { code?: string; message?: string; field?: string }[] };
        }
        console.warn("Supabase rpc manual_assign_schedule fallback error:", errorOld);
        return null;
    }
    console.warn("Supabase rpc manual_assign_schedule error:", error);
    return null;
}

// ============================================================================
// 13. Weekly Timetable (주간 시간표)
// ============================================================================

export type TTWeeklyPeriodRange = {
    start_date: string;
    end_date: string;
    day_count: number;
};

function mapWeeklyScheduleRow(d: any) {
    const teacherObj = Array.isArray(d.teacher) ? d.teacher[0] : d.teacher;
    const subjectObj = Array.isArray(d.subject) ? d.subject[0] : d.subject;
    const classroomObj = Array.isArray(d.classroom) ? d.classroom[0] : d.classroom;
    return {
        ...d,
        teacher_name: teacherObj?.name || "",
        subject_name: subjectObj?.name || "",
        classroom_display: classroomObj ? `${classroomObj.grade}-${classroomObj.class_num}` : "",
        is_modified: (() => {
            if (!d.original_schedule_id) return true;
            const orig = Array.isArray(d.original) ? d.original[0] : d.original;
            if (!orig) return true;
            if (d.teacher_id !== orig.teacher_id) return true;
            if (d.subject_id !== orig.subject_id) return true;
            if (d.period !== orig.period) return true;

            const dt = new Date(d.date + "T00:00:00");
            const dow = dt.getDay();
            const dayMap: Record<number, string> = { 1: "월", 2: "화", 3: "수", 4: "목", 5: "금", 6: "토", 0: "일" };
            if (orig.day !== dayMap[dow]) return true;

            return false;
        })()
    };
}

export async function generateWeeklyTimetableInSupabase(
    schoolId: number, timetableId: number, startDate: string, endDate: string,
    options: WeeklyGenerationOptions = {},
) {
    const client = supabase;
    if (!client) return null;
    return generateWeeklyTimetableBatches(startDate, endDate, async (start, end) => {
        const timeout = AbortSignal.timeout(25_000);
        const signal = options.signal ? AbortSignal.any([timeout, options.signal]) : timeout;
        const { data, error } = await client.rpc("generate_weekly_timetable_batch", {
            p_school_id: schoolId, p_timetable_id: timetableId,
            p_start_date: start, p_end_date: end,
        }).abortSignal(signal);
        if (error) {
            const retryable = !error.code || ["57014", "55P03", "40001", "40P01"].includes(error.code) || /fetch|network|abort|timeout/i.test(error.message);
            return { success: false, retryable, error: retryable ? undefined :
                error.code === "42501" || /Access Denied/.test(error.message)
                    ? "시간표를 만들 권한이 없습니다. 관리자 계정인지 확인해 주세요."
                    : error.code === "P0001" ? error.message : "시간표를 저장하지 못했어요. 잠시 후 다시 시도해 주세요." };
        }
        return data ? data as { success: boolean; count?: number; error?: string } : { success: false, retryable: true };
    }, options);
}

export async function getWeeklyPeriodsFromSupabase(
    schoolId: number, timetableId: number
): Promise<TTWeeklyPeriodRange[] | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("get_weekly_periods", {
        p_school_id: schoolId, p_timetable_id: timetableId
    });
    if (error) { console.warn("Supabase rpc get_weekly_periods error:", error); return null; }
    if (!Array.isArray(data)) return [];
    return (data as TTWeeklyPeriodRange[]).map((p) => ({
        start_date: String(p.start_date),
        end_date: String(p.end_date),
        day_count: Number(p.day_count) || 0,
    }));
}

export async function deleteWeeklyPeriodInSupabase(
    schoolId: number, timetableId: number, startDate: string, endDate: string
): Promise<{ success: boolean; deleted?: number; error?: string } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("delete_weekly_period", {
        p_school_id: schoolId, p_timetable_id: timetableId,
        p_start_date: startDate, p_end_date: endDate
    });
    if (error) { console.warn("Supabase rpc delete_weekly_period error:", error); return { success: false, error: error.message }; }
    return data as { success: boolean; deleted?: number };
}

export async function getWeeklySchedulesFromSupabase(
    schoolId: number, timetableId: number, startDate: string, endDate: string
) {
    if (!supabase) return null;
    const pageSize = 1000;
    const all: any[] = [];
    for (let from = 0; from < 100_000; from += pageSize) {
        const { data, error } = await supabase
            .from("schedule_manager_weeklyschedule")
            .select(`
                id, school_id, timetable_id, original_schedule_id, date, period,
                teacher_id, classroom_id, subject_id, place_id, status, memo, cell_color, substitution_history, neis_override, neis_history,
                teacher:schedule_manager_teacher(name),
                subject:schedule_manager_subject(name),
                classroom:schedule_manager_classroom(grade, class_num),
                original:schedule_manager_schedule!original_schedule_id(teacher_id, subject_id, day, period)
            `)
            .eq("school_id", schoolId)
            .eq("timetable_id", timetableId)
            .gte("date", startDate)
            .lte("date", endDate)
            .order("date", { ascending: true })
            .order("period", { ascending: true })
            .order("id", { ascending: true })
            .range(from, from + pageSize - 1);

        if (error) { console.warn("Supabase getWeeklySchedules error:", error); return null; }
        const rows = data || [];
        all.push(...rows);
        if (rows.length < pageSize) break;
    }

    // Never offer swaps using a silently truncated set of conflict checks.
    if (all.length >= 100_000) {
        console.warn("Supabase weekly schedule range is too large; select a shorter range");
        return null;
    }
    return all.map(mapWeeklyScheduleRow);
}

export async function hasWeeklyTimetableInSupabase(
    schoolId: number, timetableId: number
): Promise<boolean | null> {
    if (!supabase) return null;
    const { count, error } = await supabase
        .from("schedule_manager_weeklyschedule")
        .select("id", { count: "exact", head: true })
        .eq("school_id", schoolId)
        .eq("timetable_id", timetableId);
    if (error) { console.warn("Supabase hasWeeklyTimetable error:", error); return null; }
    return (count ?? 0) > 0;
}

export async function deleteWeeklyTimetableInSupabase(
    schoolId: number, timetableId: number
): Promise<{ success: boolean } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("delete_weekly_timetable", {
        p_school_id: schoolId, p_timetable_id: timetableId
    });
    if (error) { console.warn("Supabase rpc delete_weekly_timetable error:", error); return null; }
    return data as { success: boolean };
}

export async function deleteWeeklyDateInSupabase(
    schoolId: number, timetableId: number, date: string
): Promise<{ success: boolean; cancelled?: number } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("delete_weekly_date", {
        p_school_id: schoolId, p_timetable_id: timetableId, p_date: date
    });
    if (error) { console.warn("Supabase rpc delete_weekly_date error:", error); return null; }
    return data as { success: boolean; cancelled?: number };
}

export async function restoreWeeklyDateInSupabase(
    schoolId: number, timetableId: number, date: string
): Promise<{ success: boolean; restored?: number } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("restore_weekly_date", {
        p_school_id: schoolId, p_timetable_id: timetableId, p_date: date
    });
    if (error) { console.warn("Supabase rpc restore_weekly_date error:", error); return null; }
    return data as { success: boolean; restored?: number };
}

export async function getWeeklyPeriodCountsFromSupabase(
    schoolId: number, timetableId: number
) {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("get_weekly_period_counts", {
        p_school_id: schoolId, p_timetable_id: timetableId
    });
    if (error) { console.warn("Supabase rpc get_weekly_period_counts error:", error); return null; }
    return data;
}

export async function updateWeeklyScheduleInSupabase(
    schoolId: number, scheduleId: number,
    updates: { teacher_id?: number; subject_id?: number; place_id?: number; status?: string; memo?: string | null }
): Promise<{ success: boolean } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("update_weekly_schedule", {
        p_school_id: schoolId, p_schedule_id: scheduleId,
        p_teacher_id: updates.teacher_id || null,
        p_subject_id: updates.subject_id || null,
        p_place_id: updates.place_id || null,
        p_status: updates.status || null,
        p_memo: updates.memo !== undefined ? updates.memo : null
    });
    if (error) { console.warn("Supabase rpc update_weekly_schedule error:", error); return null; }
    return data as { success: boolean };
}

export async function swapWeeklySchedulesInSupabase(
    schoolId: number, sourceId: number, targetId: number
): Promise<{ success: boolean } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("swap_weekly_schedules", {
        p_school_id: schoolId, p_source_id: sourceId, p_target_id: targetId
    });
    if (error) { console.warn("Supabase rpc swap_weekly_schedules error:", error); return null; }
    return data as { success: boolean };
}

export async function deleteWeeklyScheduleByIdInSupabase(
    schoolId: number, scheduleId: number
): Promise<{ success: boolean } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("delete_weekly_schedule_by_id", {
        p_school_id: schoolId, p_schedule_id: scheduleId
    });
    if (error) { console.warn("Supabase rpc delete_weekly_schedule_by_id error:", error); return null; }
    return data as { success: boolean };
}

export async function createWeeklyScheduleInSupabase(
    schoolId: number, timetableId: number, date: string, period: number,
    teacherId: number, classroomId: number, subjectId: number, memo?: string
): Promise<{ success: boolean; id?: number } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("create_weekly_schedule", {
        p_school_id: schoolId, p_timetable_id: timetableId,
        p_date: date, p_period: period,
        p_teacher_id: teacherId, p_classroom_id: classroomId, p_subject_id: subjectId,
        p_memo: memo || null
    });
    if (error) { console.warn("Supabase rpc create_weekly_schedule error:", error); return null; }
    return data as { success: boolean; id?: number };
}

export async function updateWeeklyScheduleMemoInSupabase(schoolId: number, scheduleId: number, memo: string | null) {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("update_weekly_schedule", {
        p_school_id: schoolId,
        p_schedule_id: scheduleId,
        p_memo: memo
    });
    if (error) { console.warn("Supabase rpc update_weekly_schedule error:", error); return null; }
    return data as { success: boolean };
}

/** Save annotations atomically without changing lesson assignments or swap history. */
export async function updateWeeklyAnnotationsInSupabase(
    schoolId: number, timetableId: number, scheduleIds: number[],
    patch: import("./timetableCellSelection").TimetableAnnotationPatch,
): Promise<{ success: boolean; rows?: import("./timetableCellSelection").TimetableAnnotationResult[]; error?: string }> {
    if (!supabase) return { success: false, error: "저장 연결을 확인해 주세요." };
    const { data, error } = await supabase.rpc("update_weekly_annotations", {
        p_school_id: schoolId, p_timetable_id: timetableId, p_schedule_ids: scheduleIds,
        p_change_color: patch.changeColor, p_color: patch.color,
        p_memo_mode: patch.memoMode, p_memo: patch.memo,
    });
    if (error) return { success: false, error: error.code === "P0001" ? error.message : "저장하지 못했습니다. 잠시 후 다시 시도해 주세요." };
    return data ?? { success: false, error: "저장 결과를 확인하지 못했습니다." };
}

/** 주간 시간표 관리 화면 전용: 교사+과목 칸 색상 목록 (학교 관리자만) */
export async function getWeeklySubjectColorsFromSupabase(
    schoolId: number
): Promise<Record<string, string> | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("get_weekly_subject_colors", {
        p_school_id: schoolId,
    });
    if (error) {
        console.warn("Supabase rpc get_weekly_subject_colors error:", error);
        return null;
    }
    const rows = data as unknown;
    if (!Array.isArray(rows)) return {};
    const map: Record<string, string> = {};
    for (const row of rows) {
        const r = row as { teacher_id?: number; subject_id?: number; color_hex?: string };
        if (
            typeof r.teacher_id === "number" &&
            typeof r.subject_id === "number" &&
            typeof r.color_hex === "string"
        ) {
            map[`${r.teacher_id}-${r.subject_id}`] = r.color_hex;
        }
    }
    return map;
}

export async function upsertWeeklySubjectColorInSupabase(
    schoolId: number,
    teacherId: number,
    subjectId: number,
    colorHex: string
): Promise<{ success: boolean } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("upsert_weekly_subject_color", {
        p_school_id: schoolId,
        p_teacher_id: teacherId,
        p_subject_id: subjectId,
        p_color_hex: colorHex,
    });
    if (error) {
        console.warn("Supabase rpc upsert_weekly_subject_color error:", error);
        return null;
    }
    return data as { success: boolean };
}

export async function deleteWeeklySubjectColorInSupabase(
    schoolId: number,
    teacherId: number,
    subjectId: number
): Promise<{ success: boolean } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("delete_weekly_subject_color", {
        p_school_id: schoolId,
        p_teacher_id: teacherId,
        p_subject_id: subjectId,
    });
    if (error) {
        console.warn("Supabase rpc delete_weekly_subject_color error:", error);
        return null;
    }
    return data as { success: boolean };
}

// ============================================================================
// 15. Public View Links
// ============================================================================

export async function createPublicLinkInSupabase(schoolId: number, timetableId: number): Promise<string | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("create_public_link", {
        p_school_id: schoolId,
        p_timetable_id: timetableId
    });
    if (error) { console.warn("Supabase rpc create_public_link error:", error); return null; }
    return data as string;
}

export async function getPublicLinkFromSupabase(schoolId: number, timetableId: number): Promise<string | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_public_link")
        .select("token")
        .eq("school_id", schoolId)
        .eq("timetable_id", timetableId)
        .eq("is_active", true)
        .maybeSingle();

    if (error) { console.warn("Supabase get getPublicLink error:", error); return null; }
    if (!data) return null;
    return data.token;
}

export async function deletePublicLinkInSupabase(token: string): Promise<boolean> {
    if (!supabase) return false;
    const { error } = await supabase.rpc("delete_public_link", { p_token: token });
    if (error) { console.warn("Supabase rpc delete_public_link error:", error); return false; }
    return true;
}

export async function getPublicTimetableInfoFromSupabase(token: string) {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("get_public_timetable_info", { p_token: token });
    if (error) { console.warn("Supabase rpc get_public_timetable_info error:", error); return null; }
    return data;
}

export async function getPublicTimetableSchedulesFromSupabase(token: string) {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("get_public_timetable_schedules", { p_token: token });
    if (error) { console.warn("Supabase rpc get_public_timetable_schedules error:", error); return null; }
    return data;
}

export async function getPublicWeeklySchedulesFromSupabase(token: string, startDate: string, endDate: string) {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("get_public_weekly_schedules", {
        p_token: token,
        p_start_date: startDate,
        p_end_date: endDate
    });
    if (error) { console.warn("Supabase rpc get_public_weekly_schedules error:", error); return null; }
    return data;
}

// ============================================================================
// Team Teaching Groups
// ============================================================================

export async function getTeamTeachingGroupsFromSupabase(schoolId: number): Promise<TTTeamTeachingGroup[] | null> {
    if (!supabase) return null;
    const { data: groups, error } = await supabase
        .from("schedule_manager_teamteachinggroup")
        .select(`
            id, school_id, subject_id, hours_per_week, is_fixed, day, period,
            schedule_manager_subject(name)
        `)
        .eq("school_id", schoolId)
        .order("id", { ascending: true });
    
    if (error || !groups) {
        console.warn("Supabase getTeamTeachingGroups error:", error);
        return null;
    }

    if (groups.length === 0) return [];

    const groupIds = groups.map(g => g.id);

    // Fetch teachers through junction manually
    const { data: teachersData } = await supabase
        .from("schedule_manager_teamteachinggroup_teachers")
        .select(`teamteachinggroup_id, teacher_id, schedule_manager_teacher(name)`)
        .in("teamteachinggroup_id", groupIds);

    // Fetch classrooms through junction manually
    const { data: classroomsData } = await supabase
        .from("schedule_manager_teamteachinggroup_classrooms")
        .select(`teamteachinggroup_id, classroom_id, schedule_manager_classroom(grade, class_num)`)
        .in("teamteachinggroup_id", groupIds);

    const tMap = (teachersData || []).reduce((acc: any, t: any) => {
        if (!acc[t.teamteachinggroup_id]) acc[t.teamteachinggroup_id] = [];
        acc[t.teamteachinggroup_id].push(t);
        return acc;
    }, {});

    const cMap = (classroomsData || []).reduce((acc: any, c: any) => {
        if (!acc[c.teamteachinggroup_id]) acc[c.teamteachinggroup_id] = [];
        acc[c.teamteachinggroup_id].push(c);
        return acc;
    }, {});
    
    return groups.map((d: any) => {
        const teachers = tMap[d.id] || [];
        const classrooms = cMap[d.id] || [];
        
        return {
            id: d.id,
            school: d.school_id,
            subject: d.subject_id,
            subject_name: d.schedule_manager_subject?.name || "",
            hours_per_week: d.hours_per_week,
            is_fixed: !!d.is_fixed,
            day: d.day,
            period: d.period,
            teacher_ids: teachers.map((t: any) => t.teacher_id),
            teacher_names: teachers.map((t: any) => t.schedule_manager_teacher?.name || ""),
            classroom_ids: classrooms.map((c: any) => c.classroom_id),
            classroom_displays: classrooms.map((c: any) => c.schedule_manager_classroom ? `${c.schedule_manager_classroom.grade}-${c.schedule_manager_classroom.class_num}` : ""),
        };
    }) as TTTeamTeachingGroup[];
}

/**
 * 단일 팀티칭 그룹의 상세를 list helper 와 동일한 모양으로 조회한다.
 * RLS 의 `school_id == admin school` 검사가 쿼리 자체에 적용되므로 권한이 없는 그룹은 결과가 비어 null 을 반환한다.
 */
export async function getTeamTeachingGroupDetailFromSupabase(
    groupId: number
): Promise<TTTeamTeachingGroup | null> {
    if (!supabase) return null;

    const { data: group, error } = await supabase
        .from("schedule_manager_teamteachinggroup")
        .select(`
            id, school_id, subject_id, hours_per_week, is_fixed, day, period,
            schedule_manager_subject(name)
        `)
        .eq("id", groupId)
        .maybeSingle();

    if (error) {
        console.warn("Supabase getTeamTeachingGroupDetail error:", error);
        return null;
    }
    if (!group) return null;

    const [{ data: teachersData }, { data: classroomsData }] = await Promise.all([
        supabase
            .from("schedule_manager_teamteachinggroup_teachers")
            .select(`teacher_id, schedule_manager_teacher(name)`)
            .eq("teamteachinggroup_id", groupId),
        supabase
            .from("schedule_manager_teamteachinggroup_classrooms")
            .select(`classroom_id, schedule_manager_classroom(grade, class_num)`)
            .eq("teamteachinggroup_id", groupId),
    ]);

    const teachers = (teachersData || []) as any[];
    const classrooms = (classroomsData || []) as any[];

    const g = group as any;
    return {
        id: g.id,
        school: g.school_id,
        subject: g.subject_id,
        subject_name: g.schedule_manager_subject?.name || "",
        hours_per_week: g.hours_per_week,
        is_fixed: !!g.is_fixed,
        day: g.day,
        period: g.period,
        teacher_ids: teachers.map((t) => t.teacher_id),
        teacher_names: teachers.map((t) => t.schedule_manager_teacher?.name || ""),
        classroom_ids: classrooms.map((c) => c.classroom_id),
        classroom_displays: classrooms.map((c) =>
            c.schedule_manager_classroom
                ? `${c.schedule_manager_classroom.grade}-${c.schedule_manager_classroom.class_num}`
                : ""
        ),
    } as TTTeamTeachingGroup;
}

export async function createTeamTeachingGroupInSupabase(
    schoolId: number, 
    data: { subject: number; hours_per_week: number; teachers: number[]; classrooms: number[]; is_fixed?: boolean; day?: string | null; period?: number | null }
): Promise<TTTeamTeachingGroup | null> {
    if (!supabase) return null;
    const { data: group, error: groupErr } = await supabase
        .from("schedule_manager_teamteachinggroup")
        .insert([{
            school_id: schoolId,
            subject_id: data.subject,
            hours_per_week: data.hours_per_week,
            is_fixed: data.is_fixed || false,
            day: data.day || null,
            period: data.period || null,
        }])
        .select()
        .single();
        
    if (groupErr || !group) {
        console.warn("Supabase createTeamTeachingGroup error:", groupErr);
        return null;
    }
    
    if (data.teachers && data.teachers.length > 0) {
        const teacherInserts = data.teachers.map(tid => ({
            teamteachinggroup_id: group.id,
            teacher_id: tid
        }));
        await supabase.from("schedule_manager_teamteachinggroup_teachers").insert(teacherInserts);
    }
    
    if (data.classrooms && data.classrooms.length > 0) {
        const classroomInserts = data.classrooms.map(cid => ({
            teamteachinggroup_id: group.id,
            classroom_id: cid
        }));
        await supabase.from("schedule_manager_teamteachinggroup_classrooms").insert(classroomInserts);
    }
    
    const groups = await getTeamTeachingGroupsFromSupabase(schoolId);
    return groups?.find(g => g.id === group.id) || null;
}

export async function updateTeamTeachingGroupInSupabase(
    groupId: number,
    data: Partial<{ subject: number; hours_per_week: number; teachers: number[]; classrooms: number[]; is_fixed: boolean; day: string | null; period: number | null }>
): Promise<TTTeamTeachingGroup | null> {
    if (!supabase) return null;
    
    const updates: any = {};
    if (data.subject !== undefined) updates.subject_id = data.subject;
    if (data.hours_per_week !== undefined) updates.hours_per_week = data.hours_per_week;
    if (data.is_fixed !== undefined) updates.is_fixed = data.is_fixed;
    if (data.day !== undefined) updates.day = data.day;
    if (data.period !== undefined) updates.period = data.period;
    
    if (Object.keys(updates).length > 0) {
        const { error } = await supabase
            .from("schedule_manager_teamteachinggroup")
            .update(updates)
            .eq("id", groupId);
        if (error) {
            console.warn("Supabase updateTeamTeachingGroup error:", error);
            return null;
        }
    }
    
    if (data.teachers !== undefined) {
        await supabase.from("schedule_manager_teamteachinggroup_teachers").delete().eq("teamteachinggroup_id", groupId);
        if (data.teachers.length > 0) {
            const teacherInserts = data.teachers.map(tid => ({
                teamteachinggroup_id: groupId,
                teacher_id: tid
            }));
            await supabase.from("schedule_manager_teamteachinggroup_teachers").insert(teacherInserts);
        }
    }
    
    if (data.classrooms !== undefined) {
        await supabase.from("schedule_manager_teamteachinggroup_classrooms").delete().eq("teamteachinggroup_id", groupId);
        if (data.classrooms.length > 0) {
            const classroomInserts = data.classrooms.map(cid => ({
                teamteachinggroup_id: groupId,
                classroom_id: cid
            }));
            await supabase.from("schedule_manager_teamteachinggroup_classrooms").insert(classroomInserts);
        }
    }
    
    const { data: group } = await supabase.from("schedule_manager_teamteachinggroup").select("school_id").eq("id", groupId).single();
    if (group) {
        const groups = await getTeamTeachingGroupsFromSupabase(group.school_id);
        return groups?.find(g => g.id === groupId) || null;
    }
    return null;
}

export async function deleteTeamTeachingGroupInSupabase(groupId: number): Promise<TimetableDeleteResult | null> {
    if (!supabase) return null;

    await supabase.from("schedule_manager_teamteachinggroup_teachers").delete().eq("teamteachinggroup_id", groupId);
    await supabase.from("schedule_manager_teamteachinggroup_classrooms").delete().eq("teamteachinggroup_id", groupId);

    const { error } = await supabase
        .from("schedule_manager_teamteachinggroup")
        .delete()
        .eq("id", groupId);

    if (error) {
        console.warn("Supabase deleteTeamTeachingGroup error:", error);
        return timetableDeleteFail("teamTeaching", error);
    }
    return timetableDeleteOk();
}

// ============================================================================
// 협업: 역할 포함 학교 목록 (내가 owner인 것 + 멤버인 것 + 교사로 연결된 것)
// ============================================================================

export async function getSchoolsWithRoleFromSupabase(): Promise<(TTSchool & { my_role: TTSchoolRole })[] | null> {
    if (!supabase) return null;

    let myDjangoId: number | null = null;
    try {
        const raw = localStorage.getItem("user_data");
        if (raw) myDjangoId = JSON.parse(raw)?.id ?? null;
    } catch { /* */ }
    if (myDjangoId == null) return [];

    const [schoolsRes, membershipsRes, linkedTeacherRes] = await Promise.all([
        supabase
            .from("schedule_manager_school")
            .select(TIMETABLE_SCHOOL_SELECT)
            .order("created_at", { ascending: false }),
        supabase
            .from("schedule_manager_school_member")
            .select("school_id, role, user_id")
            .eq("user_id", myDjangoId),
        supabase
            .from("schedule_manager_teacher")
            .select("school_id")
            .eq("user_id", myDjangoId),
    ]);

    if (schoolsRes.error) {
        console.warn("Supabase getSchoolsWithRole error:", schoolsRes.error);
        return null;
    }
    if (membershipsRes.error) {
        console.warn("Supabase getSchoolsWithRole memberships error:", membershipsRes.error);
        return null;
    }
    if (linkedTeacherRes.error) {
        console.warn("Supabase getSchoolsWithRole teacher-link error:", linkedTeacherRes.error);
        return null;
    }

    const mapped = (schoolsRes.data ?? []).map((d: any) => ({
        ...d,
        teacher_count: (d.schedule_manager_teacher?.[0]?.count ?? 0) as number,
        classroom_count: (d.schedule_manager_classroom?.[0]?.count ?? 0) as number,
        timetable_count: (d.schedule_manager_timetable?.[0]?.count ?? 0) as number,
        has_active_timetable: false,
        admin_id: d.admin_id as number,
    }));

    return attachMySchoolRoles(
        mapped,
        myDjangoId,
        membershipsRes.data ?? [],
        (linkedTeacherRes.data ?? []).map((t: { school_id: number }) => t.school_id),
    ) as (TTSchool & { my_role: TTSchoolRole })[];
}

export type TTAdminSchool = TTSchool & {
    admin_id: number;
    owner_username?: string | null;
    owner_email?: string | null;
    owner_nickname?: string | null;
};

export type TTAdminSchoolsPage = {
    schools: TTAdminSchool[];
    total: number;
    hasMore: boolean;
};

const TT_ADMIN_PAGE_SIZE = 24;

function escapeIlikeTerm(term: string): string {
    return term.replace(/[%_\\]/g, "\\$&");
}

/** Platform superuser only (RLS). Paginated list/search of all timetable schools with owner info. */
export async function getAllSchoolsForAdmin(options?: {
    search?: string;
    offset?: number;
    limit?: number;
}): Promise<TTAdminSchoolsPage | null> {
    if (!supabase) return null;

    const term = options?.search?.trim() ?? "";
    const offset = Math.max(0, options?.offset ?? 0);
    const limit = Math.max(1, options?.limit ?? TT_ADMIN_PAGE_SIZE);
    const schoolSelect = TIMETABLE_SCHOOL_SELECT;

    let matchedOwnerIds: number[] = [];
    if (term) {
        const pattern = `%${escapeIlikeTerm(term)}%`;
        const userFilters = [
            `username.ilike."${pattern}"`,
            `email.ilike."${pattern}"`,
        ];
        const asId = Number(term);
        if (Number.isInteger(asId) && asId > 0 && String(asId) === term) {
            userFilters.push(`id.eq.${asId}`);
        }

        const [{ data: matchedUsers, error: usersSearchError }, { data: matchedProfiles, error: nickSearchError }] =
            await Promise.all([
                supabase.from("auth_user").select("id").or(userFilters.join(",")).limit(200),
                supabase.from("common_profile").select("user_id").ilike("nickname", pattern).limit(200),
            ]);

        if (usersSearchError) {
            console.warn("Supabase getAllSchoolsForAdmin owner search error:", usersSearchError);
        }
        if (nickSearchError) {
            console.warn("Supabase getAllSchoolsForAdmin nickname search error:", nickSearchError);
        }

        const seen = new Set<number>();
        for (const u of matchedUsers ?? []) {
            if (typeof u.id === "number" && !seen.has(u.id)) {
                seen.add(u.id);
                matchedOwnerIds.push(u.id);
            }
        }
        for (const p of matchedProfiles ?? []) {
            const id = p.user_id as number | null;
            if (typeof id === "number" && !seen.has(id)) {
                seen.add(id);
                matchedOwnerIds.push(id);
            }
        }

        if (Number.isInteger(asId) && asId > 0 && String(asId) === term && !seen.has(asId)) {
            matchedOwnerIds.push(asId);
        }
    }

    let query = supabase
        .from("schedule_manager_school")
        .select(schoolSelect, { count: "exact" })
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

    if (term) {
        const pattern = `%${escapeIlikeTerm(term)}%`;
        const schoolFilters = [`name.ilike."${pattern}"`];
        if (matchedOwnerIds.length > 0) {
            schoolFilters.push(`admin_id.in.(${matchedOwnerIds.join(",")})`);
        }
        query = query.or(schoolFilters.join(","));
    }

    const { data, error, count } = await query;
    if (error) {
        console.warn("Supabase getAllSchoolsForAdmin error:", error);
        return null;
    }

    const rows = data ?? [];
    const adminIds = [...new Set(rows.map((d: any) => d.admin_id).filter((id: unknown) => typeof id === "number"))];

    const ownerMap = new Map<number, { username: string; email: string; nickname: string | null }>();
    if (adminIds.length > 0) {
        const [{ data: users, error: usersError }, { data: profiles, error: profilesError }] = await Promise.all([
            supabase.from("auth_user").select("id, username, email").in("id", adminIds),
            supabase.from("common_profile").select("user_id, nickname").in("user_id", adminIds),
        ]);
        if (usersError) {
            console.warn("Supabase getAllSchoolsForAdmin owners error:", usersError);
        }
        if (profilesError) {
            console.warn("Supabase getAllSchoolsForAdmin owner nicknames error:", profilesError);
        }
        const nicknameByUserId = new Map<number, string | null>();
        for (const p of profiles ?? []) {
            if (typeof p.user_id === "number") {
                nicknameByUserId.set(p.user_id, typeof p.nickname === "string" ? p.nickname : null);
            }
        }
        for (const u of users ?? []) {
            ownerMap.set(u.id, {
                username: u.username,
                email: u.email,
                nickname: nicknameByUserId.get(u.id) ?? null,
            });
        }
    }

    const schools = rows.map((d: any) => {
        const owner = ownerMap.get(d.admin_id);
        return {
            ...d,
            teacher_count: (d.schedule_manager_teacher?.[0]?.count ?? 0) as number,
            classroom_count: (d.schedule_manager_classroom?.[0]?.count ?? 0) as number,
            timetable_count: (d.schedule_manager_timetable?.[0]?.count ?? 0) as number,
            has_active_timetable: false,
            admin_id: d.admin_id as number,
            owner_username: owner?.username ?? null,
            owner_email: owner?.email ?? null,
            owner_nickname: owner?.nickname ?? null,
        } as TTAdminSchool;
    });

    const total = count ?? schools.length;
    return {
        schools,
        total,
        hasMore: offset + schools.length < total,
    };
}

export async function getSchoolRoleFromSupabase(schoolId: number): Promise<TTSchoolRole | null> {
    if (!supabase) return null;

    const { data, error } = await supabase.rpc("tt_school_role", { p_school_id: schoolId });
    if (!error && data) return data as TTSchoolRole;

    const schools = await getSchoolsWithRoleFromSupabase();
    const school = schools?.find((s) => s.id === schoolId);
    return school?.my_role ?? null;
}

// ============================================================================
// 협업: 교체 요청 워크플로우
// ============================================================================

export async function createSwapRequestInSupabase(
    schoolId: number,
    timetableId: number,
    sourceId: number,
    targetId: number,
    message?: string
): Promise<{ success: boolean; request_id?: number; status?: string; error?: string } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("tt_create_swap_request", {
        p_school_id:    schoolId,
        p_timetable_id: timetableId,
        p_source_id:    sourceId,
        p_target_id:    targetId,
        p_message:      message ?? null,
    });
    if (error) { console.warn("createSwapRequest error:", error); return { success: false, error: error.message }; }
    return data as any;
}

export async function createCoverRequestInSupabase(
    schoolId: number,
    timetableId: number,
    weeklyId: number,
    teacherId: number,
    message?: string
): Promise<{ success: boolean; request_id?: number; status?: string; error?: string } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("tt_create_cover_request", {
        p_school_id: schoolId,
        p_timetable_id: timetableId,
        p_weekly_id: weeklyId,
        p_teacher_id: teacherId,
        p_message: message ?? null,
    });
    if (error) {
        console.warn("createCoverRequest error:", error);
        return { success: false, error: error.message };
    }
    return data as { success: boolean; request_id?: number; status?: string; error?: string };
}

export async function applyAdminSwapInSupabase(
    schoolId: number, timetableId: number, source: import("./api").TTWeeklySchedule,
    target: import("./api").TTWeeklySchedule, message: string, neisOnly: boolean, operationId: string
): Promise<{ success: boolean; request?: TTSwapRequest; error?: string } | null> {
    if (!supabase) return null;
    const { weeklyRevision } = await import("./timetableNeis");
    const { data, error } = await supabase.rpc("tt_apply_admin_swap", {
        p_school_id: schoolId, p_timetable_id: timetableId, p_source_id: source.id, p_target_id: target.id,
        p_message: message, p_neis_only: neisOnly, p_operation_id: operationId,
        p_source_revision: weeklyRevision(source), p_target_revision: weeklyRevision(target),
    });
    if (error) return { success: false, error: error.message };
    return data;
}

export type TTAppliedMoveOperation = {
    id: string; applied_at: string; neis_only: boolean;
    moves: { fromId: number; toId: number;
        from: { id: number; date: string; period: number; classroom_id: number; classroom_display: string; teacher_name: string; subject_name: string };
        to: { id: number; date: string; period: number; classroom_id: number };
    }[];
};

export async function getAppliedMovesInSupabase(schoolId: number, timetableId: number, start: string, end: string): Promise<{
    success: boolean; error?: string; operations?: TTAppliedMoveOperation[];
}> {
    if (!supabase) return { success: false, error: "서버에 연결할 수 없습니다." };
    const { data, error } = await supabase.rpc("tt_applied_move_history", {
        p_school_id: schoolId, p_timetable_id: timetableId, p_start: start, p_end: end,
    });
    return error ? { success: false, error: error.message } : { success: true, operations: data?.operations ?? [] };
}

export async function applyTeacherCoverInSupabase(
    schoolId: number, timetableId: number, lesson: import("./api").TTWeeklySchedule,
    teacherId: number, reason: string, operationId: string,
): Promise<{ success: boolean; error?: string; row?: import("./api").TTWeeklySchedule }> {
    if (!supabase) return { success: false, error: "서버에 연결할 수 없습니다." };
    const { weeklyRevision } = await import("./timetableNeis");
    const { data, error } = await supabase.rpc("tt_apply_teacher_cover", {
        p_school_id: schoolId, p_timetable_id: timetableId, p_weekly_id: lesson.id,
        p_teacher_id: teacherId, p_reason: reason, p_operation_id: operationId,
        p_revision: weeklyRevision(lesson),
    });
    if (error) return { success: false, error: error.message };
    return data?.success ? { success: true, row: data.row ? mapWeeklyScheduleRow(data.row) : undefined }
        : { success: false, error: "대강을 저장하지 못했습니다." };
}

export async function applyChainSwapInSupabase(
    schoolId: number, timetableId: number, lessons: import("./api").TTWeeklySchedule[],
    moves: import("./timetableChainSwap").ChainMove[], operationId: string,
    requiredMoves?: import("./timetableChainSwap").ChainMove[],
): Promise<{ success: boolean; error?: string } | null> {
    if (!supabase) return null;
    const { weeklyRevision } = await import("./timetableNeis");
    const byId = new Map(lessons.map((lesson) => [lesson.id, lesson]));
    const { data, error } = await supabase.rpc("tt_apply_chain_swap", {
        p_school_id: schoolId, p_timetable_id: timetableId, p_operation_id: operationId,
        p_moves: moves.map((move) => ({ from_id: move.fromId, to_id: move.toId, revision: weeklyRevision(byId.get(move.fromId)!), ...(requiredMoves?.some((m) => m.fromId === move.fromId && m.toId === move.toId) ? { required: true } : {}) })),
    });
    if (error) return { success: false, error: error.message };
    return data;
}

export async function createChainSwapRequestInSupabase(
    schoolId: number, timetableId: number, sourceId: number,
    lessons: import("./api").TTWeeklySchedule[], moves: import("./timetableChainSwap").ChainMove[],
    operationId: string, message: string,
): Promise<{ success: boolean; request_id?: number; error?: string } | null> {
    if (!supabase) return null;
    const { weeklyRevision } = await import("./timetableNeis");
    const byId = new Map(lessons.map((lesson) => [lesson.id, lesson]));
    const { data, error } = await supabase.rpc("tt_create_chain_swap_request", {
        p_school_id: schoolId, p_timetable_id: timetableId, p_source_id: sourceId,
        p_operation_id: operationId, p_message: message || null,
        p_moves: moves.map((move) => ({ from_id: move.fromId, to_id: move.toId, revision: weeklyRevision(byId.get(move.fromId)!) })),
    });
    if (error) return { success: false, error: error.message };
    return data;
}

export async function approveSwapCounterpartInSupabase(
    requestId: number
): Promise<{ success: boolean; error?: string } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("tt_approve_swap_counterpart", { p_request_id: requestId });
    if (error) { console.warn("approveSwapCounterpart error:", error); return { success: false, error: error.message }; }
    return data as any;
}

export async function rejectSwapInSupabase(
    requestId: number,
    reason?: string
): Promise<{ success: boolean; error?: string } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("tt_reject_swap", {
        p_request_id: requestId,
        p_reason: reason ?? null,
    });
    if (error) { console.warn("rejectSwap error:", error); return { success: false, error: error.message }; }
    return data as any;
}

export async function revertSwapInSupabase(requestId: number, reason?: string): Promise<{ success: boolean; error?: string } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("tt_revert_swap", { p_request_id: requestId, p_reason: reason ?? null });
    if (error) return { success: false, error: error.message };
    return data;
}

export async function deleteClosedSwapInSupabase(requestId: number): Promise<{ success: boolean; error?: string } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("tt_delete_closed_swap", { p_request_id: requestId });
    if (error) return { success: false, error: error.message };
    return data;
}

export async function approveSwapAdminInSupabase(
    requestId: number
): Promise<{ success: boolean; error?: string } | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc("tt_approve_swap_admin", { p_request_id: requestId });
    if (error) { console.warn("approveSwapAdmin error:", error); return { success: false, error: error.message }; }
    return data as any;
}

export async function getSwapRequestsFromSupabase(schoolId: number): Promise<TTSwapRequest[] | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
        .from("schedule_manager_swap_request")
        .select(`
            *,
            requester:requester_user_id ( id ),
            source_ws:source_weekly_id ( date, period, teacher_id ),
            target_ws:target_weekly_id ( date, period, teacher_id ),
            src_teacher:source_teacher_id ( name ),
            tgt_teacher:target_teacher_id ( name )
        `)
        .eq("school_id", schoolId)
        .order("created_at", { ascending: false });
    if (error) { console.warn("getSwapRequests error:", error); return null; }
    return (data ?? []).map((r: any) => ({
        ...r,
        source_date:        r.source_snapshot?.date ?? r.source_ws?.date,
        source_period:      r.source_snapshot?.period ?? r.source_ws?.period,
        source_teacher_name: r.source_snapshot?.teacher_name ?? r.src_teacher?.name,
        target_date:        r.target_snapshot?.date ?? r.target_ws?.date,
        target_period:      r.target_snapshot?.period ?? r.target_ws?.period,
        target_teacher_name: r.target_snapshot?.teacher_name ?? r.tgt_teacher?.name,
    }));
}

export type TTSwapMessage = {
    id: number;
    request_id: number;
    author_id: number | null;
    author_name: string;
    kind: "message" | "event";
    body: string;
    created_at: string;
};

export async function getSwapMessagesFromSupabase(requestId: number): Promise<TTSwapMessage[]> {
    if (!supabase) throw new Error("연결을 확인해 주세요.");
    const { data, error } = await supabase.from("schedule_manager_swap_message")
        .select("id, request_id, author_id, author_name, kind, body, created_at")
        .eq("request_id", requestId).order("id", { ascending: true });
    if (error) throw new Error("대화를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
    return data ?? [];
}

export async function sendSwapMessageInSupabase(requestId: number, body: string): Promise<void> {
    if (!supabase) throw new Error("연결을 확인해 주세요.");
    const { data, error } = await supabase.rpc("tt_send_swap_message", { p_request_id: requestId, p_body: body });
    if (error) throw new Error(error.message);
    if (!data?.success) throw new Error("메시지를 보내지 못했습니다.");
}

/** Shared date notes are scoped by the timetable's school RLS policies. */
export async function getTimetableDateNotesFromSupabase(timetableId: number, start: string, end: string): Promise<Record<string, string>> {
    if (!supabase) throw new Error("날짜 메모 연결을 확인해 주세요.");
    const { data, error } = await supabase.from("schedule_manager_date_note")
        .select("date,memo").eq("timetable_id", timetableId).gte("date", start).lte("date", end);
    if (error) throw new Error("날짜 메모를 불러오지 못했습니다.");
    return Object.fromEntries((data ?? []).map(row => [row.date, row.memo]));
}

export async function saveTimetableDateNoteInSupabase(timetableId: number, date: string, memo: string): Promise<void> {
    if (!supabase) throw new Error("저장 연결을 확인해 주세요.");
    const text = memo.trim();
    if (text.length > 1000) throw new Error("날짜 메모는 1,000자 이내로 입력해 주세요.");
    const result = text
        ? await supabase.from("schedule_manager_date_note").upsert({ timetable_id: timetableId, date, memo: text }, { onConflict: "timetable_id,date" }).select("date").single()
        : await supabase.from("schedule_manager_date_note").delete().eq("timetable_id", timetableId).eq("date", date);
    if (result.error) throw new Error("날짜 메모를 저장하지 못했습니다. 권한과 연결을 확인한 뒤 다시 시도해 주세요.");
}
